import React, {
  useCallback,
  useRef,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import moment from "moment";
import Link from "./link/Link";
import Logo from "./logo/Logo";
import stylez from "./header.module.scss";
// import stylezSuperAdmin from './headerStylesSuperAdmin.module.scss';
import UserProfile from "./user-profile/UserProfile";
import RolePermission from "../role-permission/RolePermission";
import { UserRoleEnum } from "../../types";
import { useAnalytics, Events, useUser, useToast } from "@hooks";
import { useRolesPermissions } from "../../hooks/useRolesPermission";
import ParentUserProfile from "./parent-user-profile/ParentUserProfile";
import { useLoginMutation } from "../../hooks/useLoginMutation";
import OfficeHourModal from "./OfficeHourModal";
import TranslationBadge from "../translation-provider/TranslationBadge";
import {
  useCalendarEventsLazyQuery,
  useListLessonsInProgressLazyQuery,
  useResetAllLessonProgressMutation,
} from "../../api/graphql-codegen";
import UnitProgressModal from "@components/unit-progress-modal/UnitProgressModal";
import LessonProgressModal from "@src/components/lesson-progress-modal/LessonProgressModal";
import SchoolNewYearModal from "@src/pages/lesson-progress/school-new-year/SchoolNewYearModal";
import {
  isHomeEDApp,
  IntercomCheckListID,
  IconDownArrow,
  IconUpArrow,
  CreatingResilientClassroomsId,
  IconWellbeingFoundations,
  IconKickOffCall,
  IconGroupOnboarding,
  GroupOnboardingId,
  IconSkillBuilder,
  IconTransitions,
  IconWellbeingFoundation,
  ProgramID,
  IconDashboard,
  IconGetting,
  IconLogout,
  IconOffice,
  IconUsers,
  IconHeLinks,
  IconHeUsers,
  IconGroups,
  IconLessons,
  IconUnits,
  IconCourses,
  IconAccounts,
  IconArrow,
  IconInfo,
  IconNav,
  IconSchoolLeader,
  BookStudyUnitName,
  IconProfessional,
} from "@constants";
import type { MenuProps } from "antd";
import "./customStyle.css";
import { MenuOutlined } from "@ant-design/icons";
import Button from "../button/Button";
import { Menu, Tooltip } from "antd";
import CloseIcon from "../../assets/icons/close.svg?react";
import MenuLink from "./link/MenuLink";
import SubMenuLink from "./link/SubMenuLink";
import MenuContent from "./MenuContent";
import TSFLogo from "@src/assets/icons/TSF_logo.svg?react";
import { LordIcon } from "../lord-icon/lord-icon";
import { useIntercom } from "react-use-intercom";
import IconOutline from "src/assets/icons/new-outline-info.svg?react";
import IconMouse from "./assets/mouse.svg?react";
import { InviteSlidingPanel } from "@src/pages/admin/users/invite-slide/inviteSlidePanel";
import { InviteSlidingPanel as SAInviteSlidePanel } from "@src/pages/super-admin/users/invite-slide/inviteSlidePanel";
import Spinner from "../spinner/Spinner";
import ResetModal from "@src/pages/lesson-progress/reset-modal/ResetModal";
import { useAuthToken } from "@src/hooks/useAuthToken";
import ImageOutlineJumping from "@src/pages/dashboard/assets/outline-jumping.svg?url";

import {
  WellBeingUnitId,
  EducatorOnboardingId,
  PlatformTowerId,
  StudentWellBeingSurveyId,
  IconGettingStarted,
  IconStandalone,
  IconMyCollection,
  IconAdminTop,
  IconUserManage,
  IconInviteStaff,
  IconViewUsage,
  IconLessonProgress,
  IconReportTop,
  IconExitTicket,
  IconStudentWellbeing,
  IconEducatorOnboarding,
  IconStudentIntroduction,
  IconPreProgramQuiz,
  IconClassRoomTop,
  IconAdultResource,
  IconCaregiver,
  IconEducator,
  IconHandbook,
  IconHelp,
  IconChat,
  IconSupport,
  IconMyAccount,
  IconCaregiverIntroduction,
  IconResilent,
} from "@src/constants";
import GradeSelectModal from "../grade-select-modal/GradeSelectModal";

const colors = { primary: "#FFB073", secondary: "#6C969A" };

type HeaderProps = {
  isParentApp?: boolean;
  inviteUser?: () => void;
  bulkInvite?: () => void;
  maintain?: boolean;
  isHidden?: boolean;
  isAussie?: boolean;
  reLoadData?: () => void;
};

const menuText = {
  courses: "Courses",
  accounts: "Accounts",
  lessons: "Lessons",
  invite: "Invite Staff",
  admin: "Admin",
  superAdmin: "Super Admin",
  bulkInviteText: "Bulk Invite",
  homeEd: "Home Ed",
  HomeEdUsers: "HE Users",
  HomeEdLinks: "HE Links",
  staff: "Staff",
  manageStaff: "Manage Staff",
  dashboard: "Dashboard",
  help: "Help",
  helpCentre: "Help Centre",
  profile: "Profile",
  logout: "Logout",
  community: "Community",
  groups: "Groups",
  units: "Units",
  hours: "Office Hours",
  wellBeing: "Educator Wellbeing",
  exitTicketReport: "Exit Ticket Reports",
  surveysExport: "Surveys Export",
  copyRegistrationLink: "Registration Link",
  reports: "REPORTS",
  opSA: "OP SA",
  myCourse: "My Courses",
};

function Header({
  isParentApp,
  maintain = false,
  isHidden = false,
  isAussie = false,
  reLoadData,
}: HeaderProps) {
  const { pathname } = useLocation();
  const history = useHistory();
  const { successToast } = useToast();
  const { showFirstResetLesson, setShowFirstResetLesson } = useAuthToken();
  const [getCalendarEvent] = useCalendarEventsLazyQuery({
    fetchPolicy: "network-only",
  });
  const timer = useRef<NodeJS.Timeout | null>(null);
  // type of user?
  // const isAdminOrSuper = useRolesPermissions([UserRoleEnum.SuperAdmin, UserRoleEnum.Admin]);
  const isSuperAdmin = useRolesPermissions([UserRoleEnum.SuperAdmin]);
  const isContentAdmin = useRolesPermissions([UserRoleEnum.ContentAdmin]);
  const isAdmin = useRolesPermissions([
    UserRoleEnum.Admin,
    UserRoleEnum.DistrictAdmin,
  ]);
  const isAdminOrUser = useRolesPermissions([
    UserRoleEnum.Admin,
    UserRoleEnum.DistrictAdmin,
    UserRoleEnum.User,
  ]);
  const isJustUser = useMemo(
    () => !isAdmin && !isSuperAdmin,
    [isAdmin, isSuperAdmin]
  );
  const isDistrictAdmin = useRolesPermissions([UserRoleEnum.DistrictAdmin]);
  const isParentAppCheck = isParentApp || pathname.startsWith("/parent");
  const [, logout] = useLoginMutation(!!isParentAppCheck);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [inMeeting, setInMeeting] = useState(false);
  const [showUnitModal, setShowUnitModal] = useState(false);

  const handleCloseModal = useCallback(() => {
    setShowUnitModal(false);
    setIsPostProgram(false);
    setAssessments(false);
  }, []);

  const [mobileWidth, styles] = useMemo(() => {
    let mobileWidth = 768;
    let styles = stylez;

    // set custom sizes and stylesheet for different users
    // if (isParentAppCheck) {
    //   mobileWidth = 915;
    // } else if (isSuperAdmin) {
    //   mobileWidth = 800;
    //   // styles = stylezSuperAdmin;
    // } else if (isAdmin) {
    //   mobileWidth = 600;
    // } else if (isJustUser) {
    //   mobileWidth = 600;
    // }
    return [mobileWidth, styles];
  }, [isAdmin, isJustUser, isParentAppCheck, isSuperAdmin]);

  // isMobile
  const isMobile = useMediaQuery({ maxWidth: mobileWidth });
  
  // Determine if sidebar should be fixed (dashboard page, but not on mobile)
  const isSidebarFixed = useMemo(() => {
    if (isMobile) return false; // Always use slide-in on mobile
    return pathname === '/dashboard' || pathname.startsWith('/dashboard/');
  }, [pathname, isMobile]);
  const isPhone = useMemo(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const regexs = [
      /(Android)(.+)(Mobile)/i,
      /BlackBerry/i,
      /iPhone|iPod/i,
      /Opera Mini/i,
      /IEMobile/i,
    ];
    return regexs.some((b) => userAgent.match(b));
  }, []);

  const screenWidth = -412;

  const { trackEvent } = useAnalytics();
  const [isMenuOpen, toggleMenu] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);

  const [showMenuView, setShowMenuView] = useState(false);

  const [user] = useUser();
  const { show, isOpen } = useIntercom();

  const [isDashboard, setIsDashboard] = useState(false);
  const [isUserManagement, setIsUserManagement] = useState(false);
  const [isSuperAdminTools, setIsSuperAdminTools] = useState(false);
  const [isContentAdminTools, setIsContentAdminTools] = useState(false);
  const [isHomeEdTools, setIsHomeEdTools] = useState(false);
  const [isOnboardingGuides, setIsOnboardingGuides] = useState(false);
  const [isGettingStarted, setIsGettingStarted] = useState(false);
  const [isstudyGuidesAssessmentReports, setIsStudyGuidesAssessmentReports] =
    useState(false);
  const [isClassroomLessons, setIsClassroomLessons] = useState(false);
  const [isEducatorCaregiverResources, setIsEducatorCaregiverResources] =
    useState(false);
  const [isSupport, setIsSupport] = useState(false);
  const [isMyAccount, setIsMyAccount] = useState(false);

  const [isManagement, setIsManagement] = useState(false);
  const [isInviteStaff, setIsInviteStaff] = useState(false);
  const [isAccount, setIsAccount] = useState(false);
  const [isUsageInsight, setIsUsageInsight] = useState(false);
  const [isCourses, setIsCourses] = useState(false);
  const [isUnits, setIsUnits] = useState(false);
  const [isLessons, setIsLessons] = useState(false);
  const [isGroups, setIsGroups] = useState(false);
  const [isCollections, setIsCollections] = useState(false);
  const [isMultiCollections, setIsMultiCollections] = useState(false);
  const [isPrograms, setIsPrograms] = useState(false);
  const [isHeUsers, setIsHeUsers] = useState(false);
  const [isHeLinks, setIsHeLinks] = useState(false);

  const [isResourceHub, setIsResourceHub] = useState(false);
  const [isOnboardingHandbook, setIsOnboardingHandbook] = useState(false);
  const [isCaregiverIntroduction, setIsCaregiverIntroduction] = useState(false);
  const [isGroupOnboarding, setIsGroupOnboarding] = useState(false);
  const [isKickOffCall, setIsKickOffCall] = useState(false);
  const [isScopeSequence, setIsScopeSequence] = useState(false);
  const [isEducatorOnboarding, setIsEducatorOnboarding] = useState(false);
  const [isPlatformTour, setIsPlatformTour] = useState(false);
  const [isGettingStartedGuide, setIsGettingStartedGuide] = useState(false);
  const [isSummaryReports, setIsSummaryReports] = useState(false);
  const [isAsseessmentsStudyGuides, setIsAsseessmentsStudyGuides] =
    useState(false);
  const [isStudentWellbeing, setIsStudentWellbeing] = useState(false);
  const [isStudentIntroduction, setIsStudentIntroduction] = useState(false);
  const [isBrainBreaks, setIsBrainBreaks] = useState(false);
  const [isStudentPreProgramQuiz, setIsStudentPreProgramQuiz] = useState(false);
  const [isStudentPostProgramQuiz, setIsStudentPostProgramQuiz] =
    useState(false);
  const [isPostProgram, setIsPostProgram] = useState(false);
  const [isMyCollections, setIsMyCollections] = useState(false);
  const [isStandaloneLessons, setIsStandaloneLessons] = useState(false);
  const [isMultiLessonUnits, setIsMultiLessonUnits] = useState(false);
  const [isEducatorWellbeing, setIsEducatorWellbeing] = useState(false);
  const [isCreatingResilient, setIsCreatingResilient] = useState(false);
  const [isCaregiverResources, setIsCaregiverResources] = useState(false);
  const [isBookStudy, setIsBookStudy] = useState(false);
  const [isHelpCentre, setIsHelpCentre] = useState(false);
  const [isLiveChat, setIsLiveChat] = useState(false);
  const [isViewActiveLessons, setIsViewActiveLessons] = useState(false);
  const [isMyProfile, setIsMyProfile] = useState(false);
  const [isPrivacyPolicy, setIsPrivacyPolicy] = useState(false);
  const [isTermsofUse, setIsTermsofUse] = useState(false);

  const [isWellBeingFoundation, setIsWellBeingFoundation] = useState(false);
  const [isSkillBuilder, setIsSkillBuilder] = useState(false);

  const [isOffice, setIsOffice] = useState(false);
  const [isLogout, setIsLogout] = useState(false);
  const [inviteSlide, setInviteSlide] = useState(false);
  const [scrollTopPos, setScrollTopPos] = useState(0);
  const [showDownButtom, setShowDownButton] = useState(false);
  const [resetAllLesson, setResetAllLesson] = useState(false);

  const [unitId, setUnitId] = useState("");
  const [assessments, setAssessments] = useState(false);
  const [lessonId, setLessonId] = useState("");
  const [showPlatformTower, setShowPlatformTower] = useState(false);
  const [showAusPreProgram, setShowAusPreProgram] = useState(false);
  const [showSelectGradeModal, setShowSelectGradeModal] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState("");
  const [showSchoolNewYearModal, setShowSchoolNewYearModal] = useState(false);
  const [isBookCrows, setIsBookCrows] = useState(false);
  const [isSupporting, setIsSupporting] = useState(false);

  const isTomAccount = useMemo(() => {
    if (user) {
      return user?.email === "tom@openparachuteschools.com" ||
        user?.email === "robert@rekordio.com" ||
        user?.email === "robert+superadmin@rekordio.com"
        ? true
        : false;
    }
    return false;
  }, [user]);

  const isTrial = useMemo(
    () => user?.customer?.trial ?? false,
    [user?.customer]
  );
  const isSouthAU = useMemo(
    () => (user?.customer?.state === "South Australia" ? true : false),
    [user?.customer]
  );

  useEffect(() => {
    if (isAussie) {
      if (!!user) {
        const diffDays = moment()
          .startOf("y")
          .diff(
            moment(
              user.previousLoginAt ? parseInt(user.previousLoginAt, 10) : null
            ),
            "days"
          );
        if (diffDays > 0) {
          const curYear = new Date().getFullYear();
          if (!!user.schoolNewYearInfo) {
            try {
              const schoolNewYearInfo = JSON.parse(
                user.schoolNewYearInfo
              ) as Record<string, boolean>;
              if (!schoolNewYearInfo[curYear]) {
                setShowSchoolNewYearModal(true);
              }
            } catch (error) {
              console.group("--- err: ", error);
            }
          } else {
            setShowSchoolNewYearModal(true);
          }
        }
      }
    }
  }, [isAussie, user]);

  const isTSFAussie = useMemo(() => {
    const groups = user?.schools?.[0]?.groupIds?.split(",");
    return groups?.includes("7");
  }, [user]);

  const isAUuser = useMemo(() => {
    return user?.id?.toLowerCase().startsWith("au");
  }, [user?.id]);
  const isCAuser = useMemo(() => {
    return user?.id?.toLowerCase().startsWith("ca");
  }, [user?.id]);
  const isUSuser = useMemo(() => {
    return user?.id?.toLowerCase().startsWith("us");
  }, [user?.id]);
  const isPrimary = useMemo(() => {
    return user?.schools?.[0]?.isPrimary ?? false;
  }, [user?.schools]);

  // valid accounts when created before August 1, 2023.
  const validUser = useMemo(() => {
    if (user?.createdAt) {
      const baseDate = new Date("August 1, 2023").getTime();
      return (
        baseDate - parseInt(user.createdAt) > 0 &&
        !showFirstResetLesson &&
        !pathname.startsWith("/lessonprogress")
      );
    }
    return false;
  }, [user?.createdAt, showFirstResetLesson, pathname]);

  const [listLessonsInProgress, { data, loading, refetch }] =
    useListLessonsInProgressLazyQuery({ fetchPolicy: "network-only" });
  const [resetAllLessonProgress, { loading: deleting }] =
    useResetAllLessonProgressMutation({
      onCompleted(res) {
        if (res.resetAllLessonProgress) {
          successToast(`Lesson Progress has reset Successfully!`);
          refetch();
        }
        setResetAllLesson(false);
      },
      onError(e) {
        successToast(e.message);
      },
    });

  useEffect(() => {
    if (!isAUuser) {
      // listLessonsInProgress();
    }
  }, [listLessonsInProgress, isAUuser]);

  const lessonProgressBadge = useMemo(() => {
    return data?.listLessonsInProgress?.length ?? 0;
  }, [data?.listLessonsInProgress]);

  const handleResetAllProgress = useCallback(() => {
    resetAllLessonProgress();
  }, [resetAllLessonProgress]);

  const handleOpenResetModal = () => {
    setShowFirstResetLesson("Yes");
    setResetAllLesson(true);
  };

  const gotoLessonProgress = useCallback(() => {
    setShowFirstResetLesson("Yes");
    setResetAllLesson(false);
    history.replace("/lessonprogress");
  }, [setShowFirstResetLesson, history]);

  const handleOfficeHours = (e?: any) => {
    e && e.preventDefault();
    if (isMenuOpen) toggleMenu(false);
    setShowSupportModal(true);
  };

  const checkIfInMeeting = useCallback(async () => {
    const data = (await getCalendarEvent()).data?.calendarEvents;
    const isInMeeting =
      !!data?.clinical.isInMeeting || !!data?.onboarding.isInMeeting;
    const timeLeft = Math.min(
      data?.clinical.timeLeftInSec || 0,
      data?.onboarding.timeLeftInSec || 0
    );
    if (isInMeeting !== inMeeting) {
      setInMeeting(isInMeeting);
    }
    if (timeLeft > 0) {
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = setTimeout(async () => {
        const data = (await getCalendarEvent()).data?.calendarEvents;
        const isInMeeting =
          !!data?.clinical.isInMeeting || !!data?.onboarding.isInMeeting;
        if (isInMeeting !== inMeeting) {
          setInMeeting(isInMeeting);
        }
      }, (timeLeft + 1) * 1000);
    } else if (isInMeeting && timeLeft < 0) {
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = setTimeout(async () => {
        const data = (await getCalendarEvent()).data?.calendarEvents;
        const isInMeeting =
          !!data?.clinical.isInMeeting || !!data?.onboarding.isInMeeting;
        if (isInMeeting !== inMeeting) {
          setInMeeting(isInMeeting);
        }
      }, (timeLeft + 31 * 60) * 1000);
    }
  }, [getCalendarEvent, inMeeting]);

  useEffect(() => {
    if (isHomeEDApp) {
      return;
    }
    // const intervalHandler = setInterval(checkIfInMeeting, FetchingInterval * 1000);
    checkIfInMeeting();
    // return () => {
    //   if (intervalHandler) {
    //     clearInterval(intervalHandler);
    //   }
    // }
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, [checkIfInMeeting]);

  const VertDivider = () => <div className={styles.headerDivider} />;

  const closeSupportModal = () => {
    setShowSupportModal(false);
  };

  const inviteUserHandler = useCallback(() => {
    if (showMenuView) setShowMenuView(false);
    setInviteSlide(true);
  }, [showMenuView]);

  const openUsagePage = useCallback(() => {
    if (isSuperAdmin) {
      history.push("/super/usage");
    } else if (isAdmin || isDistrictAdmin) {
      history.push("/admin/usage");
    } else return;
  }, [history, isSuperAdmin, isAdmin, isDistrictAdmin]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "auto";
    document.getElementById("root")!.style.overflow = isMenuOpen
      ? "hidden"
      : "auto";
  }, [isMenuOpen]);

  const handleScroll = () => {
    const elementMenu = document.getElementById("menuList");
    if (elementMenu) {
      const scrollTop = elementMenu.scrollTop;
      const isNearBottom =
        elementMenu.scrollTop + elementMenu.clientHeight <
        elementMenu.scrollHeight;

      setScrollTopPos(scrollTop);
      setShowDownButton(isNearBottom);
    }
  };

  useEffect(() => {
    const elementMenu = document.getElementById("menuList");
    if (elementMenu) {
      const scrollTop = elementMenu.scrollTop;
      const isNearBottom =
        elementMenu.scrollTop + elementMenu.clientHeight <
        elementMenu.scrollHeight;

      setScrollTopPos(scrollTop);
      setShowDownButton(isNearBottom);
    }
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleScrollUp = useCallback(() => {
    const elementMenu = document.getElementById("menuList");
    if (elementMenu) {
      elementMenu.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, []);

  const handleScrollDown = useCallback(() => {
    const elementMenu = document.getElementById("endMenu");
    if (elementMenu) {
      elementMenu.scrollIntoView({
        behavior: "smooth",
      });
    }
  }, []);

  const closeOverlay = () => {
    toggleMenu(false);
  };

  const openWellBeingUnitModal = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      setShowMenuView(false);
      setUnitId(WellBeingUnitId);
      setShowUnitModal(true);
      trackEvent(Events.actions.educatorWellbeingUnitOpen, {
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
    },
    [trackEvent, user]
  );

  const openProfessionalDevelopment = useCallback(() => {
    if (isAussie) {
      history.push(
        `/program/professionaldevelopment?pid=${ProgramID.AUPROFESSIONALDEV}`
      );
    } else {
      history.push(
        `/program/professionaldevelopment?pid=${ProgramID.PROFESSIONALDEV}`
      );
    }
  }, [history, isAussie]);

  const openWellbeingFoundations = useCallback(
    (tab: string) => {
      if (pathname.startsWith("/wellbeingfoundations")) {
        onMenuClose();
      }
      history.replace(`/wellbeingfoundations?tab=${tab}`);
    },
    [history, pathname]
  );

  const openCaregiver = useCallback(() => {
    if (pathname.startsWith("/caregiver")) {
      onMenuClose();
    }
    history.replace("/caregiver");
  }, [history, pathname]);

  const openCurriculumMapping = () => {
    if (isAussie) {
      window.open(
        "https://help.openparachuteschools.com/en/articles/6949377-curriculum-mapping",
        "_blank"
      );
      return false;
    }
  };

  const openBookResilience = () => {
    if (isAussie) {
      window.open(
        "https://calendly.com/adelaidecrows-foundation/open-parachute-wellbeing-visit?month=2025-08",
        "_blank"
      );
      return false;
    }
  };

  const openHandbook = () => {
    setShowMenuView(false);
    if (isAUuser) {
      window.open(
        "https://cdn.openparachuteschools.com/pdf/Open+Parachute+-+Onboarding+Handbook+AU.pdf",
        "_blank"
      );
      return false;
    } else {
      window.open(
        "https://cdn.openparachuteschools.com/pdf/Onboarding+Handbook+OP.pdf",
        "_blank"
      );
      return false;
    }
  };

  const openEducatorOnboardingProgram = useCallback(() => {
    history.push(
      `/program/educatoronboarding?pid=${ProgramID.EDUCATORONBOARDING}`
    );
  }, [history]);

  const openBookStudy = useCallback(() => {
    history.push(`/${BookStudyUnitName}`);
  }, [history]);

  const openSupportingStudentBook = () => {
    window.open(
      "https://www2.gov.bc.ca/assets/gov/erase/documents/mental-health-wellness/supporting-students-in-times-of-change.pdf",
      "_blank"
    );
  };

  const openSchoolLeaderOnboarding = () => {
    if (isTrial || isAussie) {
      return;
    }

    window.open(
      "https://learn.openparachuteschools.com/standalone/school-leader-onboarding",
      "_blank"
    );
  };

  const openDistrictLeaderOnboarding = () => {
    if (isTrial || isAussie) {
      return;
    }

    window.open(
      "https://learn.openparachuteschools.com/standalone/district-leader-onboarding",
      "_blank"
    );
  };

  const openCaregiverIntroduction = () => {
    setShowMenuView(false);
    if (isUSuser) {
      window.open(
        "https://cdn.openparachuteschools.com/pdf/Open+Parachute+Wellbeing+Resources+for+Caregivers+US.pdf",
        "_blank"
      );
      return false;
    }
    if (isCAuser) {
      window.open(
        "https://cdn.openparachuteschools.com/pdf/Open+Parachute+Wellbeing+Resources+for+Caregivers+CA.pdf",
        "_blank"
      );
      return false;
    }
    if (isAUuser) {
      window.open(
        "https://cdn.openparachuteschools.com/pdf/Open+Parachute+Wellbeing+Resources+AU.pdf",
        "_blank"
      );
      return false;
    }
  };

  const openGroupOnboardingUnit = useCallback(() => {
    setUnitId(GroupOnboardingId);
    setAssessments(false);
    setShowUnitModal(true);
  }, []);

  const openScheduleKickOffCall = () => {
    if (isTrial) {
      return;
    }
    if (isAussie) {
      window.open(
        "https://calendly.com/openparachute-rikki/kickoff-call",
        "_blank"
      );
      return false;
    } else {
      window.open(
        "https://calendly.com/madison-hilliard/30min?month=2025-07",
        "_blank"
      );
      return false;
    }
  };

  const openScopeSequece = () => {
    if (isAussie) return;
    window.open(
      "https://help.openparachuteschools.com/en/collections/9385363-scope-sequence",
      "_blank"
    );
  };

  const openEducatorOnboardingUnit = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    setShowMenuView(false);
    window.open(
      "https://help.openparachuteschools.com/en/collections/10400596-quickstart-for-educators",
      "_blank"
    );
  }, []);

  const openPlatformTour = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    setShowMenuView(false);
    setLessonId(EducatorOnboardingId);
    setShowPlatformTower(true);
  }, []);

  const intercomGettingStarted = () => {
    setShowMenuView(false);
    if (isAUuser) {
      window.open(
        "https://cdn.openparachuteschools.com/pdf/getting-started-guide-au.pdf",
        "_blank"
      );
      return;
    } else {
      window.open(
        "https://help.openparachuteschools.com/en/collections/10703611-roadmaps",
        "_blank"
      );
      return;
    }
  };

  const openExitTicketSummary = useCallback(() => {
    setShowMenuView(false);
    if (isSuperAdmin) {
      history.push("/super/survey-exports");
    } else if (isAdminOrUser) {
      history.push("/summaryreports");
    }
    trackEvent(Events.views.exitTicketReports, {
      schoolId: user?.schools?.[0]?.id,
      schoolName: user?.schools?.[0]?.name,
      userId: user?.id,
      userName: `${user?.firstName} ${user?.lastName}`,
    });
  }, [history, isAdminOrUser, isSuperAdmin, user, trackEvent]);

  const openStudentWellBeingUnit = useCallback((e?: any) => {
    e.preventDefault();
    e.stopPropagation();

    setShowMenuView(false);
    setUnitId(StudentWellBeingSurveyId);
    setAssessments(false);
    setShowUnitModal(true);
  }, []);

  const openAssessmentsStudyguides = useCallback(() => {
    if (isCAuser || isUSuser) return;
    setShowMenuView(false);
    history.push("/assessments?tab=1");
  }, [history, isCAuser, isUSuser]);

  const openNAPrePromQuizUnit = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      setShowMenuView(false);
      setUnitId("");
      setIsPostProgram(false);
      setAssessments(true);
      setShowUnitModal(true);

      trackEvent(Events.actions.preProgramQuizOpen, {
        unit_name: "Pre-Program Quiz",
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
    },
    [user, trackEvent]
  );

  const openAUPrePromQuizUnit = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    setShowMenuView(false);
    setUnitId("");
    setLessonId("");
    setShowSelectGradeModal(true);
  }, []);

  const openNAPostProgramQuizUnit = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      setShowMenuView(false);
      setAssessments(false);
      setIsPostProgram(true);
      setUnitId("");
      setLessonId("");
      setShowUnitModal(true);

      trackEvent(Events.actions.preProgramQuizOpen, {
        unit_name: "Post-Program Quiz",
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
    },
    [user, trackEvent]
  );

  const handleOpenAusPreProgramQuiz = useCallback(
    (programId: string) => {
      if (programId) {
        setSelectedGrade(programId);
        setShowAusPreProgram(true);
        setShowSelectGradeModal(false);
      }
      trackEvent(Events.actions.preProgramQuizOpen, {
        unit_name: "Pre-Program Quiz",
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
    },
    [user, trackEvent]
  );

  const onMenuClose = () => {
    setShowMenuView(false);
  };

  const openMyCourses = () => {
    if (pathname.startsWith("/lessons")) {
      onMenuClose();
    }
    history.replace("/lessons?tab=programs");
  };

  const openTransitions = () => {
    if (pathname.startsWith("/program/transitions")) {
      onMenuClose();
    }
    history.push(`/program/transitions?pid=${ProgramID.AUSTRANSITIONS}`);
  };

  const openLessonLibrary = () => {
    if (pathname.startsWith("/lessons")) {
      onMenuClose();
    }
    history.replace("/lessons?tab=library");
  };

  const openIntercom = (e?: any) => {
    // e.preventDefault();
    // e.stopPropagation();
    onMenuClose();
    show();
  };

  const openLessonProgress = useCallback(() => {
    onMenuClose();
    history.push("/lessonprogress");
  }, [history]);

  const triggerIntercomChecklist = (e?: any) => {
    e?.preventDefault();
    e?.stopPropagation();
    onMenuClose();
    if ((window as any).Intercom && isOpen) {
      (window as any).Intercom("startChecklist", IntercomCheckListID);
    }
  };

  // serve parent app menu first, for safety reasons
  if (isParentAppCheck) {
    return (
      <div className={`${styles.wrapper} ${isMenuOpen ? styles.openMenu : ""}`}>
        <div
          className={`${styles.darkOverlayHidden} ${styles.mobileOverlay}`}
          onClick={closeOverlay}
        />
        <div className={styles.logo}>
          <Logo isParentApp={isParentAppCheck} />
        </div>
        <div className={styles.links}>
          {!isPhone && (
            <Link to={"/parent/dashboard"}>{menuText.dashboard}</Link>
          )}
          {!isPhone && <VertDivider />}
          <ParentUserProfile isMobile={isPhone} menuText={menuText} />
        </div>
      </div>
    );
  }

  // New Slide Menu
  const userManageHandler = () => {
    setShowMenuView(false);
    if (isSuperAdmin) {
      history.push("/super/users");
    } else {
      history.push("/admin/users");
    }
  };

  const surveyExportsHandler = () => {
    setShowMenuView(false);
    history.push("/super/survey-exports");
  };

  const userProgramHandler = () => {
    setShowMenuView(false);
    history.push("/units/studentvoiceseries");
  };

  const goToDashboard = () => {
    setShowMenuView(false);
    history.push("/dashboard");
  };

  const dashboardMenu: MenuProps["items"] = [
    {
      key: "sub1",
    },
  ];

  const DashboardItems: MenuProps["items"] = [
    {
      key: "dashboard",
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
          onClick={goToDashboard}
          onMouseOver={() => {
            setIsDashboard(true);
          }}
          onMouseLeave={() => {
            setIsDashboard(false);
          }}
        >
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div className={styles.menuLeftIcon}>
              <LordIcon
                size={30}
                src={IconDashboard}
                isHover={isDashboard}
                target=".ant-menu-submenu-title"
              />
            </div>
            <span
              style={{
                marginLeft: 12,
              }}
            >
              {"My Dashboard"}
            </span>
          </div>
        </div>
      ),
      // children: dashboardMenu,
    },
  ];

  const userManagementMenu: MenuProps["items"] = [
    {
      key: "sub1",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconUserManage}
              isHover={isManagement}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          onClick={userManageHandler}
          onMouseOver={() => setIsManagement(true)}
          onMouseLeave={() => setIsManagement(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isManagement ? "#FFB073" : "#274649" }}
          >
            {"User Management"}
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    Manage all your staff. Reset Passwords, check their usage,
                    delete accounts.
                  </div>
                  <div className={styles.span2}>
                    <strong>Hint: </strong>See the last time a staff member
                    logged in, who hasn’t accepted their invite, and what
                    Lessons they have run!
                  </div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isManagement}
                  colors={colors}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </SubMenuLink>
      ),
    },
    {
      key: "sub2",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconInviteStaff}
              isHover={isInviteStaff}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          onClick={inviteUserHandler}
          onMouseOver={() => setIsInviteStaff(true)}
          onMouseLeave={() => setIsInviteStaff(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isInviteStaff ? "#FFB073" : "#274649" }}
          >
            {"Invite Staff"}
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    Click here to open the Invite Panel, where you can invite
                    staff individually or in bulk.
                  </div>
                  <div className={styles.span2}>
                    <strong>Hint: </strong>There’s 3 ways to get staff access;
                    Individual Invites; Bulk Invites; and a Registration Link
                  </div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isInviteStaff}
                  colors={colors}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </SubMenuLink>
      ),
    },
    isSuperAdmin
      ? {
        key: "sub3",
        label: (
          <SubMenuLink
            icon={
              <LordIcon
                size={30}
                src={IconAccounts}
                isHover={isAccount}
                target=".link_otherWrapper__Uil\+L"
              />
            }
            to={"/super/accounts"}
            onClick={() => {
              setShowMenuView(false);
            }}
            onMouseOver={() => setIsAccount(true)}
            onMouseLeave={() => setIsAccount(false)}
          >
            <div
              className={styles.menuItemSubTitle}
              style={{ color: isAccount ? "#FFB073" : "#274649" }}
            >
              {"Accounts"}
            </div>
          </SubMenuLink>
        ),
      }
      : { key: "sub3" },
    {
      key: "sub4",
      label: (
        <div
          className={styles.specialMenuItem}
          style={{
            cursor:
              !isSuperAdmin && !isAdmin && !isDistrictAdmin
                ? "default"
                : "pointer",
          }}
          onMouseOver={() => setIsUsageInsight(true)}
          onMouseLeave={() => setIsUsageInsight(false)}
          onClick={openUsagePage}
        >
          <div className={styles.menuLeftIcon}>
            <LordIcon
              size={30}
              src={IconViewUsage}
              disabled={
                !isSuperAdmin && !isAdmin && !isDistrictAdmin ? true : false
              }
              isHover={isUsageInsight}
              target=".header_specialMenuItem__fuTxa"
            />
          </div>
          <div
            className={styles.menuItemSubTitle}
            style={{
              color:
                !isSuperAdmin && !isAdmin && !isDistrictAdmin
                  ? "#D9D9D9"
                  : isUsageInsight
                    ? "#FFB073"
                    : "#274649",
              whiteSpace: "pre-wrap",
              lineHeight: 1.4,
            }}
          >
            {`Usage Insights`}

            {!isSuperAdmin && !isAdmin && !isDistrictAdmin && (
              <div className={styles.comingSoonLabel}>
                COMING
                <br />
                SOON
              </div>
            )}
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    View all your staff's usage in one place. Track which staff
                    are most active, which grade levels have the highest use,
                    and how many lessons have been completed!
                  </div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isUsageInsight}
                  colors={{ primary: "#FFB073", secondary: "#fff" }}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </div>
      ),
    },
  ];

  const userManagementItems: MenuProps["items"] = [
    {
      key: "userManagement",
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
          onMouseOver={() => {
            setIsUserManagement(true);
          }}
          onMouseLeave={() => {
            setIsUserManagement(false);
          }}
        >
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div className={styles.menuLeftIcon}>
              <LordIcon
                size={30}
                src={IconAdminTop}
                isHover={isUserManagement}
                target=".ant-menu-submenu-title"
              />
            </div>
            <span
              style={{
                marginLeft: 12,
                color: isUserManagement ? "#FFB073" : "#274649",
              }}
            >
              {`User Management`}
            </span>
          </div>
          <div className={styles.menuRightIcon}>
            <LordIcon
              size={30}
              src={IconArrow}
              isHover={isUserManagement}
              colors={colors}
              target=".ant-menu-submenu-title"
            />
          </div>
        </div>
      ),
      children: userManagementMenu,
    },
  ];

  const superAdminToolsMenu: MenuProps["items"] = [
    {
      key: "sub0",
      label: isSuperAdmin ? (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconReportTop}
              isHover={false}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          to={"/super/report-management"}
          onClick={() => {
            setShowMenuView(false);
          }}
          onMouseOver={() => { }}
          onMouseLeave={() => { }}
        >
          <div className={styles.menuItemSubTitle} style={{ color: "#274649" }}>
            {"Report Management"}
          </div>
        </SubMenuLink>
      ) : null,
    },
    {
      key: "sub00a",
      label:
        isSuperAdmin || isContentAdmin ? (
          <SubMenuLink
            icon={
              <LordIcon
                size={30}
                src="https://cdn.lordicon.com/avcownel.json"
                isHover={true}
                target=".link_otherWrapper__Uil\+L"
                colors={{ primary: "#274649", secondary: "#FFB073" }}
              />
            }
            to={"/super/sentry"}
            onClick={() => {
              setShowMenuView(false);
            }}
            onMouseOver={() => { }}
            onMouseLeave={() => { }}
          >
            <div
              className={styles.menuItemSubTitle}
              style={{ color: "#274649" }}
            >
              {"Sentry Tracking"}
            </div>
          </SubMenuLink>
        ) : null,
    },
    {
      key: "sub00",
      label: isTomAccount ? (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconExitTicket}
              isHover={false}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          to={"/super/survey-exports"}
          onClick={() => {
            setShowMenuView(false);
          }}
          onMouseOver={() => { }}
          onMouseLeave={() => { }}
        >
          <div className={styles.menuItemSubTitle} style={{ color: "#274649" }}>
            {"Survey Exports"}
          </div>
        </SubMenuLink>
      ) : null,
    },
    {
      key: "sub1",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconCourses}
              isHover={isCourses}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          to={"/super/courses"}
          onClick={() => {
            setShowMenuView(false);
          }}
          onMouseOver={() => setIsCourses(true)}
          onMouseLeave={() => setIsCourses(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isCourses ? "#FFB073" : "#274649" }}
          >
            {"Courses"}
          </div>
        </SubMenuLink>
      ),
    },
    {
      key: "sub2",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconUnits}
              isHover={isUnits}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          to={"/super/units"}
          onClick={() => {
            setShowMenuView(false);
          }}
          onMouseOver={() => setIsUnits(true)}
          onMouseLeave={() => setIsUnits(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isUnits ? "#FFB073" : "#274649" }}
          >
            {"Units"}
          </div>
        </SubMenuLink>
      ),
    },
    {
      key: "sub3",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconLessons}
              isHover={isLessons}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          to={"/super/lessons"}
          onClick={() => {
            setShowMenuView(false);
          }}
          onMouseOver={() => setIsLessons(true)}
          onMouseLeave={() => setIsLessons(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isLessons ? "#FFB073" : "#274649" }}
          >
            {"Lessons"}
          </div>
        </SubMenuLink>
      ),
    },
    {
      key: "sub4",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconGroups}
              isHover={isGroups}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          to={"/super/groups"}
          onClick={() => {
            setShowMenuView(false);
          }}
          onMouseOver={() => setIsGroups(true)}
          onMouseLeave={() => setIsGroups(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isGroups ? "#FFB073" : "#274649" }}
          >
            {"Groups"}
          </div>
        </SubMenuLink>
      ),
    },
    {
      key: "sub5",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconGroups}
              isHover={isCollections}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          to={"/super/collections"}
          onClick={() => {
            setShowMenuView(false);
          }}
          onMouseOver={() => setIsCollections(true)}
          onMouseLeave={() => setIsCollections(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isCollections ? "#FFB073" : "#274649" }}
          >
            {"Collections"}
          </div>
        </SubMenuLink>
      ),
    },
    {
      key: "sub6",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconGroups}
              isHover={isMultiCollections}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          to={"/super/multi-collections"}
          onClick={() => {
            setShowMenuView(false);
          }}
          onMouseOver={() => setIsMultiCollections(true)}
          onMouseLeave={() => setIsMultiCollections(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isMultiCollections ? "#FFB073" : "#274649" }}
          >
            {"Multi Collections"}
          </div>
        </SubMenuLink>
      ),
    },
    {
      key: "sub7",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconMyCollection}
              isHover={isPrograms}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          to={"/super/program-management"}
          onClick={() => {
            setShowMenuView(false);
          }}
          onMouseOver={() => setIsPrograms(true)}
          onMouseLeave={() => setIsPrograms(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isPrograms ? "#FFB073" : "#274649" }}
          >
            {"Programs"}
          </div>
        </SubMenuLink>
      ),
    },
  ];

  const superAdminToolsItems: MenuProps["items"] = [
    {
      key: "superAdminTools",
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
          onMouseOver={() => {
            setIsSuperAdminTools(true);
          }}
          onMouseLeave={() => {
            setIsSuperAdminTools(false);
          }}
        >
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div className={styles.menuLeftIcon}>
              <LordIcon
                size={30}
                src={IconUsers}
                isHover={isSuperAdminTools}
                target=".ant-menu-submenu-title"
              />
            </div>
            <span
              style={{
                marginLeft: 12,
              }}
            >
              {"Super Admin Tools"}
            </span>
          </div>
          <div className={styles.menuRightIcon}>
            <LordIcon
              size={30}
              src={IconArrow}
              isHover={isSuperAdminTools}
              colors={colors}
              target=".ant-menu-submenu-title"
            />
          </div>
        </div>
      ),
      children: superAdminToolsMenu,
    },
  ];

  const contentAdminToolsMenu: MenuProps["items"] = [
    {
      key: "sub1",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconUnits}
              isHover={isUnits}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          to={"/super/units"}
          onClick={() => {
            setShowMenuView(false);
          }}
          onMouseOver={() => setIsUnits(true)}
          onMouseLeave={() => setIsUnits(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isUnits ? "#FFB073" : "#274649" }}
          >
            {"Units"}
          </div>
        </SubMenuLink>
      ),
    },
    {
      key: "sub2",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconLessons}
              isHover={isLessons}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          to={"/super/lessons"}
          onClick={() => {
            setShowMenuView(false);
          }}
          onMouseOver={() => setIsLessons(true)}
          onMouseLeave={() => setIsLessons(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isLessons ? "#FFB073" : "#274649" }}
          >
            {"Lessons"}
          </div>
        </SubMenuLink>
      ),
    },
  ];

  const contentAdminToolsItems: MenuProps["items"] = [
    {
      key: "contentAdminTools",
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
          onMouseOver={() => {
            setIsContentAdminTools(true);
          }}
          onMouseLeave={() => {
            setIsContentAdminTools(false);
          }}
        >
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div className={styles.menuLeftIcon}>
              <LordIcon
                size={30}
                src={IconUsers}
                isHover={isContentAdminTools}
                target=".ant-menu-submenu-title"
              />
            </div>
            <span
              style={{
                marginLeft: 12,
              }}
            >
              {"Content Tools"}
            </span>
          </div>
          <div className={styles.menuRightIcon}>
            <LordIcon
              size={30}
              src={IconArrow}
              isHover={isContentAdminTools}
              colors={colors}
              target=".ant-menu-submenu-title"
            />
          </div>
        </div>
      ),
      children: contentAdminToolsMenu,
    },
  ];

  const homeEdMenu: MenuProps["items"] = [
    {
      key: "sub1",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconHeUsers}
              isHover={isHeUsers}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          to={"/super/heusers"}
          onClick={() => {
            setShowMenuView(false);
          }}
          onMouseOver={() => setIsHeUsers(true)}
          onMouseLeave={() => setIsHeUsers(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isHeUsers ? "#FFB073" : "#274649" }}
          >
            {"Users"}
          </div>
        </SubMenuLink>
      ),
    },
    {
      key: "sub2",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconHeLinks}
              isHover={isHeLinks}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          to={"/super/helinks"}
          onClick={() => {
            setShowMenuView(false);
          }}
          onMouseOver={() => setIsHeLinks(true)}
          onMouseLeave={() => setIsHeLinks(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isHeLinks ? "#FFB073" : "#274649" }}
          >
            {"Links"}
          </div>
        </SubMenuLink>
      ),
    },
  ];

  const homeEdItems: MenuProps["items"] = [
    {
      key: "homeEd",
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
          onMouseOver={() => {
            setIsHomeEdTools(true);
          }}
          onMouseLeave={() => {
            setIsHomeEdTools(false);
          }}
        >
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div className={styles.menuLeftIcon}>
              <LordIcon
                size={30}
                src={IconUsers}
                isHover={isHomeEdTools}
                target=".ant-menu-submenu-title"
              />
            </div>
            <span
              style={{
                marginLeft: 12,
              }}
            >
              {"Home Ed Tools"}
            </span>
          </div>
          <div className={styles.menuRightIcon}>
            <LordIcon
              size={30}
              src={IconArrow}
              isHover={isHomeEdTools}
              colors={colors}
              target=".ant-menu-submenu-title"
            />
          </div>
        </div>
      ),
      children: homeEdMenu,
    },
  ];

  const onboardingGuidesMenu: MenuProps["items"] = [
    {
      key: "sub1",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconHandbook}
              isHover={isOnboardingHandbook}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          onClick={openHandbook}
          onMouseOver={() => setIsOnboardingHandbook(true)}
          onMouseLeave={() => setIsOnboardingHandbook(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isOnboardingHandbook ? "#FFB073" : "#274649" }}
          >
            {"Onboarding Handbook"}
          </div>
          <div className={`${styles.menuRightIcon} ${styles.marginRight}`}>
            <LordIcon
              size={30}
              src={IconNav}
              isHover={isOnboardingHandbook}
              colors={colors}
              mainColor="#6C959A"
            />
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    The Onboarding Handbook is designed to provide Leadership
                    with a step-by-step guide for introducing Open Parachute to
                    your School.
                  </div>
                  <div className={styles.span2}>
                    <strong>Hint: </strong>This guide is not intended for
                    individual educators to access.
                  </div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isOnboardingHandbook}
                  colors={colors}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </SubMenuLink>
      ),
    },
    {
      key: "sub2",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconCaregiverIntroduction}
              isHover={isCaregiverIntroduction}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          onClick={openCaregiverIntroduction}
          onMouseOver={() => setIsCaregiverIntroduction(true)}
          onMouseLeave={() => setIsCaregiverIntroduction(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isCaregiverIntroduction ? "#FFB073" : "#274649" }}
          >
            {"Caregiver Introduction"}
          </div>
          <div className={`${styles.menuRightIcon} ${styles.marginRight}`}>
            <LordIcon
              size={30}
              src={IconNav}
              isHover={isCaregiverIntroduction}
              colors={colors}
              mainColor="#6C959A"
            />
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    Use this guide to introduce Open Parachute to your Caregiver
                    community.
                  </div>
                  <div className={styles.span2}>
                    <strong>Hint: </strong>Caregivers can access online
                    resources via their own Portal. The link is found in this
                    PDF!
                  </div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isCaregiverIntroduction}
                  colors={colors}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </SubMenuLink>
      ),
    },
    {
      key: "sub3",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconGroupOnboarding}
              isHover={isGroupOnboarding}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          onClick={openGroupOnboardingUnit}
          onMouseOver={() => setIsGroupOnboarding(true)}
          onMouseLeave={() => setIsGroupOnboarding(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isGroupOnboarding ? "#FFB073" : "#274649" }}
          >
            {"Group Onboarding"}
          </div>
          <div className={`${styles.menuRightIcon} ${styles.marginRight}`}>
            <LordIcon
              size={30}
              src={IconNav}
              isHover={isGroupOnboarding}
              colors={colors}
              mainColor="#6C959A"
            />
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    This Group Onboarding Presentation is identical to the
                    Educator Onboarding module, but designed to be delivered in
                    a Group setting.
                  </div>
                  <div className={styles.span2}>
                    <strong>Hint: </strong>Use this Presentation when onboarding
                    your staff as a group.
                  </div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isGroupOnboarding}
                  colors={colors}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </SubMenuLink>
      ),
    },
    {
      key: "sub4",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconKickOffCall}
              isHover={isKickOffCall}
              disabled={isTrial}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          onClick={openScheduleKickOffCall}
          onMouseOver={() => setIsKickOffCall(true)}
          onMouseLeave={() => setIsKickOffCall(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{
              color: isTrial
                ? "#D9D9D9"
                : isKickOffCall
                  ? "#FFB073"
                  : "#274649",
            }}
          >
            {"Schedule Kick-off Call"}
          </div>
          <div className={`${styles.menuRightIcon} ${styles.marginRight}`}>
            <LordIcon
              size={30}
              src={IconNav}
              isHover={isKickOffCall}
              colors={colors}
              mainColor="#6C959A"
            />
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    Book in a Video Conference call with our Team to assist with
                    Implementing Open Parachute at your school.
                  </div>
                  <div className={styles.span2}>
                    <strong>Hint: </strong>Ensure the key people who oversee
                    Wellbeing at your school can attend this call.
                  </div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isKickOffCall}
                  colors={colors}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </SubMenuLink>
      ),
    },
    {
      key: "sub5",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconPreProgramQuiz}
              isHover={isResourceHub}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          onClick={openCurriculumMapping}
          onMouseOver={() => setIsResourceHub(true)}
          onMouseLeave={() => setIsResourceHub(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isResourceHub ? "#FFB073" : "#274649" }}
          >
            {"Curriculum Mapping"}
          </div>
          <div className={`${styles.menuRightIcon} ${styles.marginRight}`}>
            <LordIcon
              size={30}
              src={IconNav}
              isHover={isResourceHub}
              colors={colors}
              mainColor="#6C959A"
            />
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    Open Parachute is aligned to both state and national
                    curriculums. Click here to view our curriculum mapping
                    guides.
                  </div>
                  <div className={styles.span2}></div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isResourceHub}
                  colors={colors}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </SubMenuLink>
      ),
    },
    (isAdmin || isDistrictAdmin) && isSouthAU
      ? {
        key: "sub6",
        label: (
          <SubMenuLink
            icon={
              <LordIcon
                size={30}
                src={IconMyCollection}
                isHover={isBookCrows}
                target=".link_otherWrapper__Uil\+L"
              />
            }
            onClick={openBookResilience}
            onMouseOver={() => setIsBookCrows(true)}
            onMouseLeave={() => setIsBookCrows(false)}
          >
            <div
              className={styles.menuItemSubTitle}
              style={{ color: isBookCrows ? "#FFB073" : "#274649" }}
            >
              {"Book Crows Resilience ..."}
            </div>
            <div className={`${styles.menuRightIcon} ${styles.marginRight}`}>
              <LordIcon
                size={30}
                src={IconNav}
                isHover={isBookCrows}
                colors={colors}
                mainColor="#6C959A"
              />
            </div>
            <Tooltip
              placement="rightTop"
              title={
                <div className={styles.cardBack}>
                  <IconOutline className={styles.infoicon} />
                  <div className={styles.hoverCardText}>
                    <div className={styles.span1}>
                      Book an{" "}
                      <strong>
                        Open Parachute x Crows Players Resilience Talk
                      </strong>{" "}
                      for AFL and AFLW players to share their experiences and
                      provide valuable insights on building resilience with
                      your students.
                    </div>
                    <div className={styles.span2}></div>
                  </div>
                </div>
              }
              overlayStyle={{ maxWidth: "400px" }}
              showArrow={false}
              color="#FFFFFF"
            >
              <div className={styles.menuRightIcon}>
                {
                  <LordIcon
                    size={30}
                    src={IconInfo}
                    isHover={isBookCrows}
                    colors={colors}
                    mainColor="#6C959A"
                  />
                }
              </div>
            </Tooltip>
          </SubMenuLink>
        ),
      }
      : {
        key: "sub6",
      },
  ];

  // NA Onboarding Resources Menu
  const onboardingResourcesMenu: MenuProps["items"] = [
    {
      key: "sub1",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconEducatorOnboarding}
              isHover={isOnboardingHandbook}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          onClick={openEducatorOnboardingProgram}
          onMouseOver={() => setIsOnboardingHandbook(true)}
          onMouseLeave={() => setIsOnboardingHandbook(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isOnboardingHandbook ? "#FFB073" : "#274649" }}
          >
            {"Educator Onboarding"}
          </div>
          <div className={`${styles.menuRightIcon} ${styles.marginRight}`}>
            <LordIcon
              size={30}
              src={IconNav}
              isHover={isOnboardingHandbook}
              colors={colors}
              mainColor="#6C959A"
            />
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    Prepare teachers to confidently deliver the program in their
                    classrooms. Learn how to navigate the platform and
                    effectively implement the lesson materials with students.
                  </div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isOnboardingHandbook}
                  colors={colors}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </SubMenuLink>
      ),
    },
    isAdmin || isDistrictAdmin || isSuperAdmin || isContentAdmin
      ? {
        key: "sub2",
        label: (
          <SubMenuLink
            icon={
              <LordIcon
                size={30}
                src={IconSchoolLeader}
                isHover={isCaregiverIntroduction}
                target=".link_otherWrapper__Uil\+L"
              />
            }
            onClick={openSchoolLeaderOnboarding}
            onMouseOver={() => setIsCaregiverIntroduction(true)}
            onMouseLeave={() => setIsCaregiverIntroduction(false)}
          >
            <div
              className={styles.menuItemSubTitle}
              style={{
                color: isCaregiverIntroduction ? "#FFB073" : "#274649",
              }}
            >
              {"School Leader Onboarding"}
            </div>
            <div className={`${styles.menuRightIcon} ${styles.marginRight}`}>
              <LordIcon
                size={30}
                src={IconNav}
                isHover={isCaregiverIntroduction}
                colors={colors}
                mainColor="#6C959A"
              />
            </div>
            <Tooltip
              placement="rightTop"
              title={
                <div className={styles.cardBack}>
                  <IconOutline className={styles.infoicon} />
                  <div className={styles.hoverCardText}>
                    <div className={styles.span1}>
                      Access a plan for implementing Open Parachute in your
                      school.
                    </div>
                  </div>
                </div>
              }
              overlayStyle={{ maxWidth: "400px" }}
              showArrow={false}
              color="#FFFFFF"
            >
              <div className={styles.menuRightIcon}>
                {
                  <LordIcon
                    size={30}
                    src={IconInfo}
                    isHover={isCaregiverIntroduction}
                    colors={colors}
                    mainColor="#6C959A"
                  />
                }
              </div>
            </Tooltip>
          </SubMenuLink>
        ),
      }
      : {
        key: "sub2",
      },
    isDistrictAdmin || isSuperAdmin || isContentAdmin
      ? {
        key: "sub3",
        label: (
          <SubMenuLink
            icon={
              <LordIcon
                size={30}
                src={IconHandbook}
                isHover={isGroupOnboarding}
                target=".link_otherWrapper__Uil\+L"
              />
            }
            onClick={openDistrictLeaderOnboarding}
            onMouseOver={() => setIsGroupOnboarding(true)}
            onMouseLeave={() => setIsGroupOnboarding(false)}
          >
            <div
              className={styles.menuItemSubTitle}
              style={{ color: isGroupOnboarding ? "#FFB073" : "#274649" }}
            >
              {"District Leader Onboarding"}
            </div>
            <div className={`${styles.menuRightIcon} ${styles.marginRight}`}>
              <LordIcon
                size={30}
                src={IconNav}
                isHover={isGroupOnboarding}
                colors={colors}
                mainColor="#6C959A"
              />
            </div>
            <Tooltip
              placement="rightTop"
              title={
                <div className={styles.cardBack}>
                  <IconOutline className={styles.infoicon} />
                  <div className={styles.hoverCardText}>
                    <div className={styles.span1}>
                      Access resources and materials for implementing Open
                      Parachute across your district.
                    </div>
                  </div>
                </div>
              }
              overlayStyle={{ maxWidth: "400px" }}
              showArrow={false}
              color="#FFFFFF"
            >
              <div className={styles.menuRightIcon}>
                {
                  <LordIcon
                    size={30}
                    src={IconInfo}
                    isHover={isGroupOnboarding}
                    colors={colors}
                    mainColor="#6C959A"
                  />
                }
              </div>
            </Tooltip>
          </SubMenuLink>
        ),
      }
      : {
        key: "sub3",
      },
  ];

  const onBoardingGuidesItems: MenuProps["items"] = [
    {
      key: "onBoardingGuides",
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
          onMouseOver={() => {
            setIsOnboardingGuides(true);
          }}
          onMouseLeave={() => {
            setIsOnboardingGuides(false);
          }}
        >
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div className={styles.menuLeftIcon}>
              <LordIcon
                size={30}
                src={IconHandbook}
                isHover={isOnboardingGuides}
                target=".ant-menu-submenu-title"
              />
            </div>
            <span
              style={{
                marginLeft: 12,
              }}
            >
              {isAUuser ? "Onboarding Guides" : "Onboarding Resources"}
            </span>
          </div>
          <div className={styles.menuRightIcon}>
            <LordIcon
              size={30}
              src={IconArrow}
              isHover={isOnboardingGuides}
              colors={colors}
              target=".ant-menu-submenu-title"
            />
          </div>
        </div>
      ),
      children: isAUuser ? onboardingGuidesMenu : onboardingResourcesMenu,
    },
  ];

  const gettingStartedMenu: MenuProps["items"] = [
    {
      key: "sub1",
      label: (
        <div
          className={styles.specialMenuItem}
          style={{ cursor: "pointer" }}
          onClick={openEducatorOnboardingUnit}
          onMouseOver={() => setIsEducatorOnboarding(true)}
          onMouseLeave={() => setIsEducatorOnboarding(false)}
        >
          <div className={styles.menuLeftIcon}>
            <LordIcon
              size={30}
              src={IconEducatorOnboarding}
              disabled={false}
              isHover={isEducatorOnboarding}
              target=".header_specialMenuItem__fuTxa"
            />
          </div>
          <div
            className={styles.menuItemSubTitle}
            style={{
              color: isEducatorOnboarding ? "#FFB073" : "#274649",
            }}
          >
            {"Educator Onboarding"}
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    Visit our Help Centre to review our Onboarding
                    documentation. These informative guides are designed to get
                    you up and running quickly!
                  </div>
                  <div className={styles.span2}>
                    <strong>Hint: </strong>If you would like to dive deeper,
                    there are plenty of in-depth resources available too!
                  </div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isEducatorOnboarding}
                  colors={colors}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </div>
      ),
    },
    {
      key: "sub2",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconSupport}
              isHover={isPlatformTour}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          onClick={openPlatformTour}
          onMouseOver={() => setIsPlatformTour(true)}
          onMouseLeave={() => setIsPlatformTour(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isPlatformTour ? "#FFB073" : "#274649" }}
          >
            {"Platform Tour"}
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    Access a video walkthrough of the entire Open Parachute
                    platform.
                  </div>
                  <div className={styles.span2}>
                    <strong>Hint: </strong>Key sections have been separated into
                    their own videos. <br />
                    Select the relevant section, or watch them all!
                  </div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isPlatformTour}
                  colors={colors}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </SubMenuLink>
      ),
    },
    {
      key: "sub3",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconGettingStarted}
              isHover={isGettingStartedGuide}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          onClick={intercomGettingStarted}
          onMouseOver={() => setIsGettingStartedGuide(true)}
          onMouseLeave={() => setIsGettingStartedGuide(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isGettingStartedGuide ? "#FFB073" : "#274649" }}
          >
            {"Getting Started Guide"}
          </div>
          <div className={`${styles.menuRightIcon} ${styles.marginRight}`}>
            <LordIcon
              size={30}
              src={IconNav}
              isHover={isGettingStartedGuide}
              colors={colors}
              mainColor="#6C959A"
            />
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    Use this Guide to get up and running with Open Parachute
                    quickly!
                  </div>
                  <div className={styles.span2}>
                    <strong>Hint: </strong>If you have already been through the
                    Educator Onboarding, you don’t need to read this!
                  </div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isGettingStartedGuide}
                  colors={colors}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </SubMenuLink>
      ),
    },
  ];

  const naGettingStartedMenu: MenuProps["items"] = [
    {
      key: "sub1",
      label: (
        <div
          className={styles.specialMenuItem}
          style={{ cursor: "pointer" }}
          onClick={openEducatorOnboardingUnit}
          onMouseOver={() => setIsEducatorOnboarding(true)}
          onMouseLeave={() => setIsEducatorOnboarding(false)}
        >
          <div className={styles.menuLeftIcon}>
            <LordIcon
              size={30}
              src={IconEducatorOnboarding}
              disabled={false}
              isHover={isEducatorOnboarding}
              target=".header_specialMenuItem__fuTxa"
            />
          </div>
          <div
            className={styles.menuItemSubTitle}
            style={{
              color: isEducatorOnboarding ? "#FFB073" : "#274649",
            }}
          >
            {"Quickstart for Educators"}
          </div>
          <div className={`${styles.menuRightIcon} ${styles.marginRight}`}>
            <LordIcon
              size={30}
              src={IconNav}
              isHover={isEducatorOnboarding}
              colors={colors}
              mainColor="#6C959A"
            />
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    Visit our Help Centre to review our Onboarding
                    documentation. These informative guides are designed to get
                    you up and running quickly!
                  </div>
                  <div className={styles.span2}>
                    <strong>Hint: </strong>If you would like to dive deeper,
                    there are plenty of in-depth resources available too!
                  </div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isEducatorOnboarding}
                  colors={colors}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </div>
      ),
    },
    {
      key: "sub2",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconPreProgramQuiz}
              isHover={isScopeSequence}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          onClick={openScopeSequece}
          onMouseOver={() => setIsScopeSequence(true)}
          onMouseLeave={() => setIsScopeSequence(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isScopeSequence ? "#FFB073" : "#274649" }}
          >
            {"Curriculum Overviews"}
          </div>
          <div className={`${styles.menuRightIcon} ${styles.marginRight}`}>
            <LordIcon
              size={30}
              src={IconNav}
              isHover={isScopeSequence}
              colors={colors}
              mainColor="#6C959A"
            />
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    View the Scope & Sequence for each Grade.
                  </div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isScopeSequence}
                  colors={colors}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </SubMenuLink>
      ),
    },
    {
      key: "sub3",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconGettingStarted}
              isHover={isGettingStartedGuide}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          onClick={intercomGettingStarted}
          onMouseOver={() => setIsGettingStartedGuide(true)}
          onMouseLeave={() => setIsGettingStartedGuide(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isGettingStartedGuide ? "#FFB073" : "#274649" }}
          >
            {"Lesson Roadmaps"}
          </div>
          <div className={`${styles.menuRightIcon} ${styles.marginRight}`}>
            <LordIcon
              size={30}
              src={IconNav}
              isHover={isGettingStartedGuide}
              colors={colors}
              mainColor="#6C959A"
            />
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    Lesson Roadmaps provide lesson recommendations for every
                    grade to help you get started quickly.
                  </div>
                  <div className={styles.span2}>
                    <strong>Hint: </strong>Select the best path based on the
                    needs and comfort level of your class!
                  </div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isGettingStartedGuide}
                  colors={colors}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </SubMenuLink>
      ),
    },
    {
      key: "sub4",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconCaregiverIntroduction}
              isHover={isCaregiverIntroduction}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          onClick={openCaregiverIntroduction}
          onMouseOver={() => setIsCaregiverIntroduction(true)}
          onMouseLeave={() => setIsCaregiverIntroduction(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isCaregiverIntroduction ? "#FFB073" : "#274649" }}
          >
            {"Caregiver Introduction"}
          </div>
          <div className={`${styles.menuRightIcon} ${styles.marginRight}`}>
            <LordIcon
              size={30}
              src={IconNav}
              isHover={isCaregiverIntroduction}
              colors={colors}
              mainColor="#6C959A"
            />
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    Use this guide to introduce Open Parachute to your Caregiver
                    community.
                  </div>
                  <div className={styles.span2}>
                    <strong>Hint: </strong>Caregivers can access online
                    resources via their own Portal. The link is found in this
                    PDF!
                  </div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isCaregiverIntroduction}
                  colors={colors}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </SubMenuLink>
      ),
    },
    {
      key: "sub5",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconKickOffCall}
              isHover={isKickOffCall}
              disabled={isTrial}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          onClick={openScheduleKickOffCall}
          onMouseOver={() => setIsKickOffCall(true)}
          onMouseLeave={() => setIsKickOffCall(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{
              color: isTrial
                ? "#D9D9D9"
                : isKickOffCall
                  ? "#FFB073"
                  : "#274649",
            }}
          >
            {"Schedule Support Call"}
          </div>
          <div className={`${styles.menuRightIcon} ${styles.marginRight}`}>
            <LordIcon
              size={30}
              src={IconNav}
              isHover={isKickOffCall}
              colors={colors}
              mainColor="#6C959A"
            />
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    Book in a Video Conference call with our Team to assist with
                    Implementing Open Parachute at your school.
                  </div>
                  <div className={styles.span2}>
                    <strong>Hint: </strong>Ensure the key people who oversee
                    Wellbeing at your school can attend this call.
                  </div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isKickOffCall}
                  colors={colors}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </SubMenuLink>
      ),
    },
  ];

  const gettingStartedItems: MenuProps["items"] = [
    {
      key: "gettingStarted",
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
          onMouseOver={() => {
            setIsGettingStarted(true);
          }}
          onMouseLeave={() => {
            setIsGettingStarted(false);
          }}
        >
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div className={styles.menuLeftIcon}>
              <LordIcon
                size={30}
                src={IconGettingStarted}
                isHover={isGettingStarted}
                target=".ant-menu-submenu-title"
              />
            </div>
            <span
              style={{
                marginLeft: 12,
              }}
            >
              {"Getting Started"}
            </span>
          </div>
          <div className={styles.menuRightIcon}>
            <LordIcon
              size={30}
              src={IconArrow}
              isHover={isGettingStarted}
              colors={colors}
              target=".ant-menu-submenu-title"
            />
          </div>
        </div>
      ),
      children: isAUuser ? gettingStartedMenu : naGettingStartedMenu,
    },
  ];

  const studyGuideMenu: MenuProps["items"] = [
    isAUuser
      ? {
        key: "sub0",
        label: (
          <div
            className={styles.specialMenuItem}
            style={{ cursor: "pointer" }}
            onClick={openAUPrePromQuizUnit}
            onMouseOver={() => setIsStudentPreProgramQuiz(true)}
            onMouseLeave={() => setIsStudentPreProgramQuiz(false)}
          >
            <div className={styles.menuLeftIcon}>
              <LordIcon
                size={30}
                src={IconPreProgramQuiz}
                isHover={isStudentPreProgramQuiz}
                target=".header_specialMenuItem__fuTxa"
              />
            </div>
            <div
              className={styles.menuItemSubTitle}
              style={{
                color: isStudentPreProgramQuiz ? "#FFB073" : "#274649",
                whiteSpace: "pre-wrap",
                lineHeight: 1.4,
              }}
            >
              {`Student Pre-\nProgram Quiz`}
            </div>
            <Tooltip
              placement="rightTop"
              title={
                <div className={styles.cardBack}>
                  <IconOutline className={styles.infoicon} />
                  <div className={styles.hoverCardText}>
                    <div className={styles.span1}>
                      This online quiz will provide you with an indication of
                      your students’ competencies, which can help guide your
                      lesson selections.
                    </div>
                    <div className={styles.span2}>
                      <strong>Hint: </strong>Students require a device to
                      complete the Quiz. It will take approximately 15
                      minutes.
                    </div>
                  </div>
                </div>
              }
              overlayStyle={{ maxWidth: "400px" }}
              showArrow={false}
              color="#FFFFFF"
            >
              <div className={styles.menuRightIcon}>
                {
                  <LordIcon
                    size={30}
                    src={IconInfo}
                    isHover={isStudentPreProgramQuiz}
                    colors={colors}
                    mainColor="#6C959A"
                  />
                }
              </div>
            </Tooltip>
          </div>
        ),
      }
      : { key: "sub0" },
    isAUuser
      ? {
        key: "sub1",
        label: (
          <SubMenuLink
            icon={
              <LordIcon
                size={30}
                src={IconExitTicket}
                isHover={isSummaryReports}
                target=".link_otherWrapper__Uil\+L"
              />
            }
            onClick={openExitTicketSummary}
            onMouseOver={() => setIsSummaryReports(true)}
            onMouseLeave={() => setIsSummaryReports(false)}
          >
            <div
              className={styles.menuItemSubTitle}
              style={{ color: isSummaryReports ? "#FFB073" : "#274649" }}
            >
              {"Summary Reports"}
            </div>
            <Tooltip
              placement="rightTop"
              title={
                <div className={styles.cardBack}>
                  <IconOutline className={styles.infoicon} />
                  <div className={styles.hoverCardText}>
                    <div className={styles.span1}>
                      {isAussie
                        ? "Export Summary Reports for the Pre-Program Quiz, Exit Tickets, and Assessments."
                        : "Export Summary Reports for the Pre-Program Quiz, Post-Program Quiz and Exit Tickets."}
                    </div>
                    <div className={styles.span2}>
                      <strong>Hint: </strong>You can filter by date to select
                      a specific period of time!
                    </div>
                  </div>
                </div>
              }
              overlayStyle={{ maxWidth: "400px" }}
              showArrow={false}
              color="#FFFFFF"
            >
              <div className={styles.menuRightIcon}>
                {
                  <LordIcon
                    size={30}
                    src={IconInfo}
                    isHover={isSummaryReports}
                    colors={colors}
                    mainColor="#6C959A"
                  />
                }
              </div>
            </Tooltip>
          </SubMenuLink>
        ),
      }
      : {
        key: "sub1",
      },
    isAUuser
      ? {
        key: "sub2",
        label: (
          <div
            className={styles.specialMenuItem}
            onClick={openAssessmentsStudyguides}
            onMouseOver={() => setIsAsseessmentsStudyGuides(true)}
            onMouseLeave={() => setIsAsseessmentsStudyGuides(false)}
          >
            <div className={styles.menuLeftIcon}>
              <LordIcon
                size={30}
                src={IconReportTop}
                isHover={isAsseessmentsStudyGuides}
                target=".header_specialMenuItem__fuTxa"
              />
            </div>
            <div
              className={styles.menuItemSubTitle}
              style={{
                color: isAsseessmentsStudyGuides ? "#FFB073" : "#274649",
                whiteSpace: "pre-wrap",
                lineHeight: 1.4,
              }}
            >
              {`Assessments & \nStudy Guides`}
            </div>
            <Tooltip
              placement="rightTop"
              title={
                <div className={styles.cardBack}>
                  <IconOutline className={styles.infoicon} />
                  <div className={styles.hoverCardText}>
                    <div className={styles.span1}>
                      Create your own Assessments based on the specific units
                      your students have completed during the year.
                    </div>
                    <div className={styles.span2}>
                      <strong>Hint: </strong>It’s important to complete the
                      Unit Study Guides, as the Assessments test knowledge
                      specifically relating to the Units!
                    </div>
                  </div>
                </div>
              }
              overlayStyle={{ maxWidth: "400px" }}
              showArrow={false}
              color="#FFFFFF"
            >
              <div className={styles.menuRightIcon}>
                {
                  <LordIcon
                    size={30}
                    src={IconInfo}
                    isHover={isAsseessmentsStudyGuides}
                    colors={colors}
                    mainColor="#6C959A"
                  />
                }
              </div>
            </Tooltip>
          </div>
        ),
      }
      : {
        key: "sub2",
        label: (
          <div
            className={styles.specialMenuItem}
            style={{ cursor: "pointer" }}
            onClick={openNAPrePromQuizUnit}
            onMouseOver={() => setIsStudentPreProgramQuiz(true)}
            onMouseLeave={() => setIsStudentPreProgramQuiz(false)}
          >
            <div className={styles.menuLeftIcon}>
              <LordIcon
                size={30}
                src={IconPreProgramQuiz}
                isHover={isStudentPreProgramQuiz}
                target=".header_specialMenuItem__fuTxa"
              />
            </div>
            <div
              className={styles.menuItemSubTitle}
              style={{
                color: isStudentPreProgramQuiz ? "#FFB073" : "#274649",
                whiteSpace: "pre-wrap",
                lineHeight: 1.4,
              }}
            >
              {`Student Pre-\nProgram Quiz`}
            </div>
            <Tooltip
              placement="rightTop"
              title={
                <div className={styles.cardBack}>
                  <IconOutline className={styles.infoicon} />
                  <div className={styles.hoverCardText}>
                    <div className={styles.span1}>
                      This online quiz will provide you with an indication of
                      your students’ competencies, which can help guide your
                      lesson selections.
                    </div>
                    <div className={styles.span2}>
                      <strong>Hint: </strong>Students require a device to
                      complete the Quiz. It will take approximately 15
                      minutes.
                    </div>
                  </div>
                </div>
              }
              overlayStyle={{ maxWidth: "400px" }}
              showArrow={false}
              color="#FFFFFF"
            >
              <div className={styles.menuRightIcon}>
                {
                  <LordIcon
                    size={30}
                    src={IconInfo}
                    isHover={isStudentPreProgramQuiz}
                    colors={colors}
                    mainColor="#6C959A"
                  />
                }
              </div>
            </Tooltip>
          </div>
        ),
      },
    isAUuser
      ? {
        key: "sub3",
      }
      : {
        key: "sub3",
        label: (
          <div
            className={styles.specialMenuItem}
            style={{ cursor: "pointer" }}
            onClick={openNAPostProgramQuizUnit}
            onMouseOver={() => setIsStudentPostProgramQuiz(true)}
            onMouseLeave={() => setIsStudentPostProgramQuiz(false)}
          >
            <div className={styles.menuLeftIcon}>
              <LordIcon
                size={30}
                src={IconPreProgramQuiz}
                isHover={isStudentPostProgramQuiz}
                target=".header_specialMenuItem__fuTxa"
              />
            </div>
            <div
              className={styles.menuItemSubTitle}
              style={{
                color: isStudentPostProgramQuiz ? "#FFB073" : "#274649",
                whiteSpace: "pre-wrap",
                lineHeight: 1.4,
              }}
            >
              {`Student Post-\nProgram Quiz`}
            </div>
            <Tooltip
              placement="rightTop"
              title={
                <div className={styles.cardBack}>
                  <IconOutline className={styles.infoicon} />
                  <div className={styles.hoverCardText}>
                    <div className={styles.span1}>
                      This online quiz will provide you with an indication of
                      your students’ growth across different competencies
                      throughout the school year.
                    </div>
                    <div className={styles.span2}>
                      <strong>Hint: </strong>Students require a device to
                      complete the Quiz. It will take approximately 15
                      minutes.
                    </div>
                  </div>
                </div>
              }
              overlayStyle={{ maxWidth: "400px" }}
              showArrow={false}
              color="#FFFFFF"
            >
              <div className={styles.menuRightIcon}>
                {
                  <LordIcon
                    size={30}
                    src={IconInfo}
                    isHover={isStudentPostProgramQuiz}
                    colors={colors}
                    mainColor="#6C959A"
                  />
                }
              </div>
            </Tooltip>
          </div>
        ),
      },
    (isSuperAdmin || isAdmin || isContentAdmin) && isAUuser
      ? {
        key: "sub4",
        label: (
          <SubMenuLink
            icon={
              <LordIcon
                size={30}
                src={IconStudentWellbeing}
                isHover={isStudentWellbeing}
                target=".link_otherWrapper__Uil\+L"
              />
            }
            onClick={openStudentWellBeingUnit}
            onMouseOver={() => setIsStudentWellbeing(true)}
            onMouseLeave={() => setIsStudentWellbeing(false)}
          >
            <div
              className={styles.menuItemSubTitle}
              style={{
                color: isStudentWellbeing ? "#FFB073" : "#274649",
                whiteSpace: "pre-wrap",
                lineHeight: 1.4,
                textAlign: "left",
              }}
            >
              {`Student Wellbeing \nInsight Report`}
            </div>
            <Tooltip
              placement="rightTop"
              title={
                <div className={styles.cardBack}>
                  <IconOutline className={styles.infoicon} />
                  <div className={styles.hoverCardText}>
                    <div className={styles.span1}>
                      The Student Wellbeing survey provides anonymous
                      self-report information about your students in Grades 4+
                    </div>
                    <div className={styles.span2}>
                      <strong>Hint: </strong>Only Account Administrators can
                      access and generate this survey.
                    </div>
                  </div>
                </div>
              }
              overlayStyle={{ maxWidth: "400px" }}
              showArrow={false}
              color="#FFFFFF"
            >
              <div className={styles.menuRightIcon}>
                {
                  <LordIcon
                    size={30}
                    src={IconInfo}
                    isHover={isStudentWellbeing}
                    colors={colors}
                    mainColor="#6C959A"
                  />
                }
              </div>
            </Tooltip>
          </SubMenuLink>
        ),
      }
      : { key: "sub4" },
    isAUuser
      ? {
        key: "sub5",
      }
      : {
        key: "sub5",
        label: (
          <SubMenuLink
            icon={
              <LordIcon
                size={30}
                src={IconExitTicket}
                isHover={isSummaryReports}
                target=".link_otherWrapper__Uil\+L"
              />
            }
            onClick={openExitTicketSummary}
            onMouseOver={() => setIsSummaryReports(true)}
            onMouseLeave={() => setIsSummaryReports(false)}
          >
            <div
              className={styles.menuItemSubTitle}
              style={{ color: isSummaryReports ? "#FFB073" : "#274649" }}
            >
              {"Summary Reports"}
            </div>
            <Tooltip
              placement="rightTop"
              title={
                <div className={styles.cardBack}>
                  <IconOutline className={styles.infoicon} />
                  <div className={styles.hoverCardText}>
                    <div className={styles.span1}>
                      {isAussie
                        ? "Export Summary Reports for the Pre-Program Quiz, Exit Tickets, and Assessments."
                        : "Export Summary Reports for the Pre-Program Quiz, Post-Program Quiz and Exit Tickets."}
                    </div>
                    <div className={styles.span2}>
                      <strong>Hint: </strong>You can filter by date to select
                      a specific period of time!
                    </div>
                  </div>
                </div>
              }
              overlayStyle={{ maxWidth: "400px" }}
              showArrow={false}
              color="#FFFFFF"
            >
              <div className={styles.menuRightIcon}>
                {
                  <LordIcon
                    size={30}
                    src={IconInfo}
                    isHover={isSummaryReports}
                    colors={colors}
                    mainColor="#6C959A"
                  />
                }
              </div>
            </Tooltip>
          </SubMenuLink>
        ),
      },
  ];

  const studyGuideItems: MenuProps["items"] = [
    {
      key: "studyGuide",
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
          onMouseOver={() => {
            setIsStudyGuidesAssessmentReports(true);
          }}
          onMouseLeave={() => {
            setIsStudyGuidesAssessmentReports(false);
          }}
        >
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div className={styles.menuLeftIcon}>
              <LordIcon
                size={30}
                src={IconReportTop}
                isHover={isstudyGuidesAssessmentReports}
                target=".ant-menu-submenu-title"
              />
            </div>
            <span
              style={{
                marginLeft: 12,
              }}
            >
              {`Assessments & Reports`}
            </span>
          </div>
          <div className={styles.menuRightIcon}>
            <LordIcon
              size={30}
              src={IconArrow}
              isHover={isstudyGuidesAssessmentReports}
              colors={colors}
              target=".ant-menu-submenu-title"
            />
          </div>
        </div>
      ),
      children: studyGuideMenu,
    },
  ];

  const wellBeingFoundationMenu: MenuProps["items"] = [
    {
      key: "sub1",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconStudentIntroduction}
              isHover={isStudentIntroduction}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          onClick={() => openWellbeingFoundations("student-introduction")}
          onMouseOver={() => setIsStudentIntroduction(true)}
          onMouseLeave={() => setIsStudentIntroduction(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isStudentIntroduction ? "#FFB073" : "#274649" }}
          >
            {"Student Introduction"}
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    Start here when first introducing Open Parachute to your
                    class.
                  </div>
                  <div className={styles.span2}>
                    <strong>Hint: </strong>Run a few Brain Brakes prior to
                    introducing the lessons to help your class feel more
                    comfortable to dive deeper into the lessons!
                  </div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            trigger={"hover"}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isStudentIntroduction}
                  colors={colors}
                  mainColor="#6C959A"
                  stroke="regular"
                />
              }
            </div>
          </Tooltip>
        </SubMenuLink>
      ),
    },
    {
      key: "sub2",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconWellbeingFoundations}
              isHover={isBrainBreaks}
              target=".link_otherWrapper__Uil\+L"
              stroke="regular"
            />
          }
          onClick={() => openWellbeingFoundations("brainbreaks")}
          onMouseOver={() => setIsBrainBreaks(true)}
          onMouseLeave={() => setIsBrainBreaks(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isBrainBreaks ? "#FFB073" : "#274649" }}
          >
            {"Brain Breaks"}
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    Brain breaks are fun & short classroom icebreakers or
                    skill-building activities, ranging from 5-15 minutes.
                  </div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            trigger={"hover"}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isBrainBreaks}
                  colors={colors}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </SubMenuLink>
      ),
    },
    {
      key: "sub3",
      label: (
        <div
          className={styles.specialMenuItem}
          style={{ cursor: "pointer" }}
          onClick={() => openWellbeingFoundations("skillbuilders")}
          onMouseOver={() => setIsSkillBuilder(true)}
          onMouseLeave={() => setIsSkillBuilder(false)}
        >
          <div className={styles.menuLeftIcon}>
            <LordIcon
              size={30}
              src={IconSkillBuilder}
              isHover={isSkillBuilder}
              target=".header_specialMenuItem__fuTxa"
              stroke="regular"
            />
          </div>
          <div
            className={styles.menuItemSubTitle}
            style={{
              color: isSkillBuilder ? "#FFB073" : "#274649",
            }}
          >
            Skill-Builders
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    Short, approachable/practical lessons intended to practice
                    well-being strategies anytime in any course. These will
                    build on skills that students have already practiced as well
                    as provide the opportunity to learn and practice new skills.
                  </div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isSkillBuilder}
                  colors={colors}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </div>
      ),
    },
  ];

  const classroomMenu: MenuProps["items"] = [
    {
      key: "sub1",
      label: (
        <div
          className={styles.specialMenuItem}
          style={{ cursor: "pointer" }}
          onClick={openLessonLibrary}
          onMouseOver={() => setIsStandaloneLessons(true)}
          onMouseLeave={() => setIsStandaloneLessons(false)}
        >
          <div className={styles.menuLeftIcon}>
            <LordIcon
              size={30}
              src={IconStandalone}
              isHover={isStandaloneLessons}
              target=".header_specialMenuItem__fuTxa"
            />
          </div>
          <div
            className={styles.menuItemSubTitle}
            style={{
              color: isStandaloneLessons ? "#FFB073" : "#274649",
            }}
          >
            {"Lesson Library"}
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  {
                    <div className={styles.span1}>
                      This is the hub for all lessons available on Open
                      Parachute. Lessons are organized by grade and further
                      categorized by theme. Search for lessons based on grade,
                      topic, lesson type, tier and competency.
                    </div>
                  }
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isStandaloneLessons}
                  colors={colors}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </div>
      ),
    },
    {
      key: "sub2",
      label: (
        <div
          className={styles.specialMenuItem}
          style={{ cursor: "pointer" }}
          onClick={openMyCourses}
          onMouseOver={() => setIsMyCollections(true)}
          onMouseLeave={() => setIsMyCollections(false)}
        >
          <div className={styles.menuLeftIcon}>
            <LordIcon
              size={30}
              src={IconMyCollection}
              isHover={isMyCollections}
              target=".header_specialMenuItem__fuTxa"
            />
          </div>
          <div
            className={styles.menuItemSubTitle}
            style={{
              color: isMyCollections ? "#FFB073" : "#274649",
            }}
          >
            {"Programs"}
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    Programs contain sequenced lessons that are designed to be
                    completed in series and allow for more depth and
                    understanding into a particular topic area.
                  </div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isMyCollections}
                  colors={colors}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </div>
      ),
    },
  ];

  const classroomAUMenu: MenuProps["items"] = [
    isPrimary
      ? {
        key: "sub1",
        label: (
          <SubMenuLink
            icon={
              <LordIcon
                size={30}
                src={IconMyCollection}
                isHover={isMultiLessonUnits}
                target=".link_otherWrapper__Uil\+L"
              />
            }
            to={"#"}
            onClick={userProgramHandler}
            onMouseOver={() => setIsMultiLessonUnits(true)}
            onMouseLeave={() => setIsMultiLessonUnits(false)}
          >
            <div
              className={styles.menuItemSubTitle}
              style={{ color: isMultiLessonUnits ? "#FFB073" : "#274649" }}
            >
              {"Student Voice Series"}
            </div>
            <Tooltip
              placement="rightTop"
              title={
                <div className={styles.cardBack}>
                  <IconOutline className={styles.infoicon} />
                  <div className={styles.hoverCardText}>
                    <div className={styles.span1}>
                      These pre-sequenced Lessons allow students to dive deep
                      into peer stories by following their journey across
                      multiple classes.
                    </div>
                    <div className={styles.span2}>
                      <strong>Hint: </strong>These Units contain multiple
                      lessons that must be run in sequence.
                    </div>
                  </div>
                </div>
              }
              overlayStyle={{ maxWidth: "400px" }}
              showArrow={false}
              color="#FFFFFF"
            >
              <div className={styles.menuRightIcon}>
                {
                  <LordIcon
                    size={30}
                    src={IconInfo}
                    isHover={isMultiLessonUnits}
                    colors={colors}
                    mainColor="#6C959A"
                  />
                }
              </div>
            </Tooltip>
          </SubMenuLink>
        ),
      }
      : {
        key: "sub1",
        label: (
          <div
            className={styles.specialMenuItem}
            style={{ cursor: "pointer" }}
            onClick={openMyCourses}
            onMouseOver={() => setIsMyCollections(true)}
            onMouseLeave={() => setIsMyCollections(false)}
          >
            <div className={styles.menuLeftIcon}>
              <LordIcon
                size={30}
                src={IconMyCollection}
                isHover={isMyCollections}
                target=".header_specialMenuItem__fuTxa"
              />
            </div>
            <div
              className={styles.menuItemSubTitle}
              style={{
                color: isMyCollections ? "#FFB073" : "#274649",
              }}
            >
              {"Programs"}
            </div>
            <Tooltip
              placement="rightTop"
              title={
                <div className={styles.cardBack}>
                  <IconOutline className={styles.infoicon} />
                  <div className={styles.hoverCardText}>
                    <div className={styles.span1}>
                      Programs contain sequenced lessons that are designed to
                      be completed in series and allow for more depth and
                      understanding into a particular topic area.
                    </div>
                  </div>
                </div>
              }
              overlayStyle={{ maxWidth: "400px" }}
              showArrow={false}
              color="#FFFFFF"
            >
              <div className={styles.menuRightIcon}>
                {
                  <LordIcon
                    size={30}
                    src={IconInfo}
                    isHover={isMyCollections}
                    colors={colors}
                    mainColor="#6C959A"
                  />
                }
              </div>
            </Tooltip>
          </div>
        ),
      },
    isPrimary
      ? {
        key: "sub2",
        label: (
          <SubMenuLink
            icon={
              <LordIcon
                size={30}
                src={IconTransitions}
                isHover={isMyCollections}
                target=".link_otherWrapper__Uil\+L"
              />
            }
            to={`/program/transitions?pid=${ProgramID.AUSTRANSITIONS}`}
            onClick={openTransitions}
            onMouseOver={() => setIsMyCollections(true)}
            onMouseLeave={() => setIsMyCollections(false)}
          >
            <div
              className={styles.menuItemSubTitle}
              style={{ color: isMyCollections ? "#FFB073" : "#274649" }}
            >
              {"Transitions™"}
            </div>
            <Tooltip
              placement="rightTop"
              title={
                <div className={styles.cardBack}>
                  <IconOutline className={styles.infoicon} />
                  <div className={styles.hoverCardText}>
                    <div className={styles.span1}>
                      <strong>Transitions™</strong> is designed to support
                      students’ wellbeing during the Middle-Year transitions
                      between grades 5 and 9.
                    </div>
                  </div>
                </div>
              }
              overlayStyle={{ maxWidth: "400px" }}
              showArrow={false}
              color="#FFFFFF"
            >
              <div className={styles.menuRightIcon}>
                {
                  <LordIcon
                    size={30}
                    src={IconInfo}
                    isHover={isMultiLessonUnits}
                    colors={colors}
                    mainColor="#6C959A"
                  />
                }
              </div>
            </Tooltip>
          </SubMenuLink>
        ),
      }
      : {
        key: "sub2",
        label: (
          <div
            className={styles.specialMenuItem}
            style={{ cursor: "pointer" }}
            onClick={openLessonLibrary}
            onMouseOver={() => setIsStandaloneLessons(true)}
            onMouseLeave={() => setIsStandaloneLessons(false)}
          >
            <div className={styles.menuLeftIcon}>
              <LordIcon
                size={30}
                src={IconStandalone}
                isHover={isStandaloneLessons}
                target=".header_specialMenuItem__fuTxa"
              />
            </div>
            <div
              className={styles.menuItemSubTitle}
              style={{
                color: isStandaloneLessons ? "#FFB073" : "#274649",
              }}
            >
              {"Standalone Lessons"}
            </div>
            <Tooltip
              placement="rightTop"
              title={
                <div className={styles.cardBack}>
                  <IconOutline className={styles.infoicon} />
                  <div className={styles.hoverCardText}>
                    {
                      <div className={styles.span1}>
                        This is the hub for all lessons available on Open
                        Parachute. Lessons are organized by grade and further
                        categorized by theme. Search for lessons based on
                        grade, topic, lesson type, tier and competency.
                      </div>
                    }
                  </div>
                </div>
              }
              overlayStyle={{ maxWidth: "400px" }}
              showArrow={false}
              color="#FFFFFF"
            >
              <div className={styles.menuRightIcon}>
                {
                  <LordIcon
                    size={30}
                    src={IconInfo}
                    isHover={isStandaloneLessons}
                    colors={colors}
                    mainColor="#6C959A"
                  />
                }
              </div>
            </Tooltip>
          </div>
        ),
      },
  ];

  const classroomItems: MenuProps["items"] = [
    {
      key: "classroom",
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
          onMouseOver={() => {
            setIsClassroomLessons(true);
          }}
          onMouseLeave={() => {
            setIsClassroomLessons(false);
          }}
        >
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div className={styles.menuLeftIcon}>
              <LordIcon
                size={30}
                src={IconClassRoomTop}
                isHover={isClassroomLessons}
                target=".ant-menu-submenu-title"
              />
            </div>
            <span
              style={{
                marginLeft: 12,
              }}
            >
              {`Classroom Lessons`}
            </span>
          </div>
          <div className={styles.menuRightIcon}>
            <LordIcon
              size={30}
              src={IconArrow}
              isHover={isClassroomLessons}
              colors={colors}
              target=".ant-menu-submenu-title"
            />
          </div>
        </div>
      ),
      children: isAUuser ? classroomAUMenu : classroomMenu,
    },
  ];

  const wellBeingMenuItems: MenuProps["items"] = [
    {
      key: "wellbeingfoundation",
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
          onMouseOver={() => {
            setIsWellBeingFoundation(true);
          }}
          onMouseLeave={() => {
            setIsWellBeingFoundation(false);
          }}
        >
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div className={styles.menuLeftIcon}>
              <LordIcon
                size={30}
                colors={colors}
                src={IconWellbeingFoundation}
                isHover={isWellBeingFoundation}
                target=".ant-menu-submenu-title"
                stroke="regular"
              />
            </div>
            <span
              style={{
                marginLeft: 12,
              }}
            >
              {`Well-Being Foundations`}
            </span>
          </div>
          <div className={styles.menuRightIcon}>
            <LordIcon
              size={30}
              src={IconArrow}
              isHover={isWellBeingFoundation}
              colors={colors}
              target=".ant-menu-submenu-title"
            />
          </div>
        </div>
      ),
      children: wellBeingFoundationMenu,
    },
  ];

  const educatorCaregiverResourcesMenu: MenuProps["items"] = [
    {
      key: "sub1",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconProfessional}
              isHover={isEducatorWellbeing}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          onClick={openProfessionalDevelopment}
          onMouseOver={() => setIsEducatorWellbeing(true)}
          onMouseLeave={() => setIsEducatorWellbeing(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isEducatorWellbeing ? "#FFB073" : "#274649" }}
          >
            Professional Development
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    Professional Development resources for Educators.
                  </div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isEducatorWellbeing}
                  colors={colors}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </SubMenuLink>
      ),
    },
    {
      key: "sub2",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconCaregiver}
              isHover={isCaregiverResources}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          onClick={openCaregiver}
          onMouseOver={() => setIsCaregiverResources(true)}
          onMouseLeave={() => setIsCaregiverResources(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isCaregiverResources ? "#FFB073" : "#274649" }}
          >
            {"Caregiver Resources"}
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    Resources for Caregivers to help support the wellbeing of
                    their children at home.
                  </div>
                  <div className={styles.span2}>
                    <strong>Hint: </strong>Caregivers can access these resources
                    using their own Portal. Use the Share button to generate a
                    link!
                  </div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isCaregiverResources}
                  colors={colors}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </SubMenuLink>
      ),
    },
    isAUuser
      ? { key: "sub3" }
      : {
        key: "sub3",
        label: (
          <SubMenuLink
            icon={
              <img
                src={ImageOutlineJumping}
                width={30}
                height={30}
                alt="Logo"
              />
            }
            onClick={openSupportingStudentBook}
            onMouseOver={() => setIsSupporting(true)}
            onMouseLeave={() => setIsSupporting(false)}
          >
            <div
              className={styles.menuItemSubTitle}
              style={{ color: isSupporting ? "#FFB073" : "#274649" }}
            >
              {"Supporting Students\nIn ..."}
            </div>
            <Tooltip
              placement="rightTop"
              title={
                <div className={styles.cardBack}>
                  <IconOutline className={styles.infoicon} />
                  <div className={styles.hoverCardText}>
                    <div className={styles.span1}>
                      Download a PDF guide for supporting students during
                      transitions.
                    </div>
                  </div>
                </div>
              }
              overlayStyle={{ maxWidth: "400px" }}
              showArrow={false}
              color="#FFFFFF"
            >
              <div className={styles.menuRightIcon}>
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isSupporting}
                  colors={colors}
                  mainColor="#6C959A"
                />
              </div>
            </Tooltip>
          </SubMenuLink>
        ),
      },
  ];

  const educatorCaregiverResourcesItems: MenuProps["items"] = [
    {
      key: "educatorCaregiverResources",
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
          onMouseOver={() => {
            setIsEducatorCaregiverResources(true);
          }}
          onMouseLeave={() => {
            setIsEducatorCaregiverResources(false);
          }}
        >
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div className={styles.menuLeftIcon}>
              <LordIcon
                size={30}
                src={IconAdultResource}
                isHover={isEducatorCaregiverResources}
                target=".ant-menu-submenu-title"
              />
            </div>
            <span
              style={{
                marginLeft: 12,
              }}
            >
              {`Educators & Caregivers`}
            </span>
          </div>
          <div className={styles.menuRightIcon}>
            <LordIcon
              size={30}
              src={IconArrow}
              isHover={isEducatorCaregiverResources}
              colors={colors}
              target=".ant-menu-submenu-title"
            />
          </div>
        </div>
      ),
      children: educatorCaregiverResourcesMenu,
    },
  ];

  const supportMenu: MenuProps["items"] = [
    {
      key: "sub1",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconHelp}
              isHover={isHelpCentre}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          href={"https://help.openparachuteschools.com/"}
          target={"_blank"}
          onMouseOver={() => setIsHelpCentre(true)}
          onMouseLeave={() => setIsHelpCentre(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isHelpCentre ? "#FFB073" : "#274649" }}
          >
            {isUSuser ? "Help Center" : "Help Centre"}
          </div>
          <div className={`${styles.menuRightIcon} ${styles.marginRight}`}>
            <LordIcon
              size={30}
              src={IconNav}
              isHover={isHelpCentre}
              colors={colors}
              mainColor="#6C959A"
              stroke={"regular"}
            />
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    Access further help and resources for getting Started.
                  </div>
                  <div className={styles.span2}>
                    <strong>Hint: </strong>The Help Centre also features guides
                    and videos for how to support struggling students.
                  </div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isHelpCentre}
                  colors={colors}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </SubMenuLink>
      ),
    },
    {
      key: "sub2",
      label: (
        <div
          className={styles.specialMenuItem}
          style={{ cursor: "point" }}
          onClick={openIntercom}
          onMouseOver={() => setIsLiveChat(true)}
          onMouseLeave={() => setIsLiveChat(false)}
        >
          <div className={styles.menuLeftIcon}>
            <LordIcon
              size={30}
              src={IconChat}
              isHover={isLiveChat}
              target=".header_specialMenuItem__fuTxa"
            />
          </div>
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isLiveChat ? "#FFB073" : "#274649" }}
          >
            {`Live Chat`}
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    Chat with a member of the Open Parachute team!
                  </div>
                  <div className={styles.span2}>
                    <strong>Hint: </strong>Ask questions about the content,
                    getting started, or any technical issues.
                  </div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isLiveChat}
                  colors={colors}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </div>
      ),
    },
  ];

  const supportItems: MenuProps["items"] = [
    {
      key: "support",
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
          onMouseOver={() => {
            setIsSupport(true);
          }}
          onMouseLeave={() => {
            setIsSupport(false);
          }}
        >
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div className={styles.menuLeftIcon}>
              <LordIcon
                size={30}
                src={IconSupport}
                isHover={isSupport}
                target=".ant-menu-submenu-title"
              />
            </div>
            <span
              style={{
                marginLeft: 12,
              }}
            >
              {`Support`}
            </span>
          </div>
          <div className={styles.menuRightIcon}>
            <LordIcon
              size={30}
              src={IconArrow}
              isHover={isSupport}
              colors={colors}
              target=".ant-menu-submenu-title"
            />
          </div>
        </div>
      ),
      children: supportMenu,
    },
  ];

  const myAccountMenu: MenuProps["items"] = [
    {
      key: "sub1",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconLessonProgress}
              isHover={isViewActiveLessons}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          onClick={openLessonProgress}
          onMouseOver={() => setIsViewActiveLessons(true)}
          onMouseLeave={() => setIsViewActiveLessons(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isViewActiveLessons ? "#FFB073" : "#274649" }}
          >
            {"View Active Lessons"}
          </div>
          <Tooltip
            placement="rightTop"
            title={
              <div className={styles.cardBack}>
                <IconOutline className={styles.infoicon} />
                <div className={styles.hoverCardText}>
                  <div className={styles.span1}>
                    View all the lessons you have begun and completed.
                  </div>
                  <div className={styles.span2}>
                    <strong>Hint: </strong>Reset your Lesson Progress Bars at
                    the beginning of each new School Year!
                  </div>
                </div>
              </div>
            }
            overlayStyle={{ maxWidth: "400px" }}
            showArrow={false}
            color="#FFFFFF"
          >
            <div className={styles.menuRightIcon}>
              {
                <LordIcon
                  size={30}
                  src={IconInfo}
                  isHover={isViewActiveLessons}
                  colors={colors}
                  mainColor="#6C959A"
                />
              }
            </div>
          </Tooltip>
        </SubMenuLink>
      ),
    },
    {
      key: "sub2",
      label: (
        <SubMenuLink
          icon={
            <LordIcon
              size={30}
              src={IconMyAccount}
              isHover={isMyProfile}
              target=".link_otherWrapper__Uil\+L"
            />
          }
          to={"/profile"}
          onClick={() => {
            setShowMenuView(false);
          }}
          onMouseOver={() => setIsMyProfile(true)}
          onMouseLeave={() => setIsMyProfile(false)}
        >
          <div
            className={styles.menuItemSubTitle}
            style={{ color: isMyProfile ? "#FFB073" : "#274649" }}
          >
            {"My Profile"}
          </div>
        </SubMenuLink>
      ),
    },
  ];

  const myAccountItems: MenuProps["items"] = [
    {
      key: "myAccount",
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
          onMouseOver={() => {
            setIsMyAccount(true);
          }}
          onMouseLeave={() => {
            setIsMyAccount(false);
          }}
        >
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div className={styles.menuLeftIcon}>
              <LordIcon
                size={30}
                src={IconMyAccount}
                isHover={isMyAccount}
                target=".ant-menu-submenu-title"
              />
            </div>
            <span
              style={{
                marginLeft: 12,
              }}
            >
              {"My Account"}
            </span>
          </div>
          <div className={styles.menuRightIcon}>
            <LordIcon
              size={30}
              src={IconArrow}
              isHover={isMyAccount}
              colors={colors}
              target=".ant-menu-submenu-title"
            />
          </div>
        </div>
      ),
      children: myAccountMenu,
    },
  ];

  const logoutMenu: MenuProps["items"] = [
    {
      key: "sub1",
    },
  ];

  const LogoutItems: MenuProps["items"] = [
    {
      key: "logOut",
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
          onClick={logout}
          onMouseOver={() => {
            setIsLogout(true);
          }}
          onMouseLeave={() => {
            setIsLogout(false);
          }}
        >
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div className={styles.menuLeftIcon}>
              <LordIcon
                size={30}
                src={IconLogout}
                isHover={isLogout}
                target=".ant-menu-submenu-title"
              />
            </div>
            <span
              style={{
                marginLeft: 12,
              }}
            >
              {"Logout"}
            </span>
          </div>
        </div>
      ),
      children: logoutMenu,
    },
  ];

  return (
    <div
      className={
        isHidden ? styles.headerContainerHidden : styles.headerContainer
      }
    >
      <div className={`${styles.wrapper} ${isMenuOpen ? styles.openMenu : ""}`}>
        <div
          className={`${styles.darkOverlayHidden} ${styles.mobileOverlay}`}
        />

        <div className={styles.leftmenu}>
          {!isSidebarFixed && (
            <Button
              label=""
              className={styles.menuButton}
              leftIcon={<MenuOutlined className={styles.burgerIcon} />}
              onClick={() => {
                setShowMenuView(true);
              }}
            />
          )}
          <div className={styles.logo}>
            <Logo isParentApp={isParentAppCheck} />
          </div>
          {isTSFAussie && (
            <div className={styles.tsfLogo}>
              <TSFLogo />
            </div>
          )}
        </div>

        {maintain ? (
          <>
            <Link href={"https://help.openparachuteschools.com/"}>
              {"SUPPORT"}
            </Link>
          </>
        ) : (
          <>
            {/*OP Super Admin menu is here*/}
            {(isSuperAdmin || isContentAdmin) && (
              <RolePermission
                permissions={[
                  UserRoleEnum.SuperAdmin,
                  UserRoleEnum.ContentAdmin,
                ]}
              >
                <div className={styles.links}>
                  <>
                    {lessonProgressBadge > 0 && validUser && (
                      <>
                        <div className={styles.lessonNotify}>
                          <span>{"Action Required!"}</span>
                          <h1>{"Reset Lesson Progress"}</h1>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                          onClick={handleOpenResetModal}
                        >
                          <div className={styles.progressBadgeView}>
                            {loading ? (
                              <Spinner width={20} height={20} />
                            ) : (
                              <>
                                {lessonProgressBadge > 0 && (
                                  <div className={styles.whiteBadge}>
                                    {lessonProgressBadge}
                                  </div>
                                )}
                                <span>..</span>
                              </>
                            )}
                          </div>
                        </div>
                        <VertDivider />
                      </>
                    )}

                    <VertDivider />
                    <div className={styles.schoolName}>{user?.schools?.[0]?.name}</div>
                    <VertDivider />

                    <TranslationBadge type="navbar" />
                    <VertDivider />
                 
                    <UserProfile
                      mobileWidth={mobileWidth}
                      menuText={menuText}
                      isHidden={isHidden}
                    />
                    {isMobile ? (
                      <>
                        <Link to={"/profile"}>{menuText.profile}</Link>
                        <Link href="#" onClick={logout}>
                          {menuText.logout}
                        </Link>
                      </>
                    ) : null}
                  </>
                </div>
              </RolePermission>
            )}
            {/*OP Super Admin menu ends here*/}

            {/*Admin menu is here*/}
            {/*<RolePermission permissions={[UserRoleEnum.SuperAdmin]}>*/}
            {isAdmin && !isSuperAdmin && (
              <RolePermission
                permissions={[UserRoleEnum.Admin, UserRoleEnum.DistrictAdmin]}
              >
                <div className={styles.links}>
                  {lessonProgressBadge > 0 && validUser && (
                    <>
                      <div className={styles.lessonNotify}>
                        <span>{"Action Required!"}</span>
                        <h1>{"Reset Lesson Progress"}</h1>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                        onClick={handleOpenResetModal}
                      >
                        <div className={styles.progressBadgeView}>
                          {loading ? (
                            <Spinner width={20} height={20} />
                          ) : (
                            <>
                              {lessonProgressBadge > 0 && (
                                <div className={styles.whiteBadge}>
                                  {lessonProgressBadge}
                                </div>
                              )}
                              <span>..</span>
                            </>
                          )}
                        </div>
                      </div>
                      <VertDivider />
                    </>
                  )}

                  <VertDivider />
                  <div className={styles.schoolName}>{user?.schools?.[0]?.name}</div>
                  <VertDivider />

                  {/* <Link
                    to={"#"}
                    onClick={() => {
                      setShowMenuView(true);
                    }}
                  >
                    {"Menu"}
                  </Link>
                  <VertDivider /> */}

                  <TranslationBadge />
                  <VertDivider />
                 
                  <UserProfile
                    mobileWidth={mobileWidth}
                    menuText={menuText}
                    isHidden={isHidden}
                  />
                  {isMobile && (
                    <>
                      <Link to={"/profile"}>{menuText.profile}</Link>
                      <Link href="#" onClick={logout}>
                        {menuText.logout}
                      </Link>
                    </>
                  )}
                </div>
              </RolePermission>
            )}
            {/*Admin menu ends here*/}

            {/*User menu is here*/}
            {isJustUser && (
              <RolePermission permissions={[UserRoleEnum.User]}>
                {/*<RolePermission permissions={[UserRoleEnum.SuperAdmin]}>*/}
                <div className={styles.links}>
                  <VertDivider />
                  <div className={styles.schoolName}>{user?.schools?.[0]?.name}</div>
                  <VertDivider />

                  {/* <Link
                    to={"#"}
                    onClick={() => {
                      setShowMenuView(true);
                    }}
                  >
                    {"Menu"}
                  </Link>
                  <VertDivider /> */}

                  <TranslationBadge type="navbar" />
                  <VertDivider />

                  <UserProfile mobileWidth={mobileWidth} menuText={menuText} />
                  {isMobile ? (
                    <>
                      <Link to={"/profile"}>{menuText.profile}</Link>
                      <Link href="#" onClick={logout}>
                        {menuText.logout}
                      </Link>
                    </>
                  ) : null}
                </div>
              </RolePermission>
            )}
          </>
        )}
        {/*User menu ends here*/}
      </div>

      {showSupportModal && (
        <OfficeHourModal open={showSupportModal} onClose={closeSupportModal} />
      )}

      {showUnitModal && (
        <UnitProgressModal
          open={showUnitModal}
          onClose={handleCloseModal}
          unitId={unitId}
          assessments={assessments}
          isPostProgram={isPostProgram}
        />
      )}

      {showPlatformTower && lessonId && (
        <LessonProgressModal
          open={showPlatformTower}
          onClose={() => setShowPlatformTower(false)}
          lessonId={PlatformTowerId}
          isPlatformTower={true}
        />
      )}

      {showSchoolNewYearModal && (
        <SchoolNewYearModal
          open={showSchoolNewYearModal}
          onClose={() => setShowSchoolNewYearModal(false)}
          onOkAction={() => history.push("/lessonprogress")}
        />
      )}

      {showAusPreProgram && (
        <LessonProgressModal
          open={showAusPreProgram}
          onClose={() => setShowAusPreProgram(false)}
          lessonId={lessonId}
          isAusPreProgramQuiz={showAusPreProgram}
          gradeName={selectedGrade}
        />
      )}

      {showSelectGradeModal && (
        <GradeSelectModal
          open={showSelectGradeModal}
          onClose={() => setShowSelectGradeModal(false)}
          onContinue={handleOpenAusPreProgramQuiz}
        />
      )}

      {resetAllLesson && (
        <ResetModal
          title={
            "Would you like to clear your Completed Lessons from last year?"
          }
          description={
            "View your Lesson Progress to clear all In Progress and Completed lessons from last year "
          }
          resetTitle={"Reset Lesson Status"}
          cancelTitle="Cancel"
          reviewLessonButton={"View Lesson Progress"}
          open={resetAllLesson}
          onClose={() => setResetAllLesson(false)}
          onReset={handleResetAllProgress}
          loading={deleting}
          goToResetLesson={gotoLessonProgress}
        />
      )}

      {(isAdmin || isDistrictAdmin) && inviteSlide && (
        <InviteSlidingPanel
          bulkInviteSlide={inviteSlide}
          setBulkInviteSlide={setInviteSlide}
          reLoadData={reLoadData}
        />
      )}

      {isSuperAdmin && inviteSlide && (
        <SAInviteSlidePanel
          bulkInviteSlide={inviteSlide}
          setBulkInviteSlide={setInviteSlide}
          reLoadData={reLoadData}
        />
      )}

      {!isSidebarFixed && showMenuView && (
        <>
          <div
            className={styles.backDrop}
            onClick={() => setShowMenuView(false)}
          />
          <button onClick={onMenuClose} className={styles.closeWrapper}>
            <CloseIcon className={styles.closeIcon} />
          </button>
        </>
      )}

      {isSidebarFixed ? (
        <div className={styles.fixedSidebar}>
          <MenuContent
            isAussie={isAussie}
            setShowMenuView={setShowMenuView}
            showCloseButton={false}
            isParentAppCheck={isParentAppCheck}
            onCloseMenu={onMenuClose}
            inviteUserHandler={inviteUserHandler}
            openStudentWellBeingUnit={openStudentWellBeingUnit}
            openExitTicketSummary={openExitTicketSummary}
            openIntercom={openIntercom}
            logout={logout}
            isAUuser={isAUuser}
          />
        </div>
      ) : (
        <div
          className={styles.mainMenuList}
          style={{
            transform: showMenuView
              ? "translateX(0)"
              : `translateX(${screenWidth}px)`,
          }}
        >
          <MenuContent
            isAussie={isAussie}
            setShowMenuView={setShowMenuView}
            showCloseButton={true}
            isParentAppCheck={isParentAppCheck}
            onCloseMenu={onMenuClose}
            inviteUserHandler={inviteUserHandler}
            openStudentWellBeingUnit={openStudentWellBeingUnit}
            openExitTicketSummary={openExitTicketSummary}
            openIntercom={openIntercom}
            logout={logout}
            isAUuser={isAUuser}
          />
        </div>
      )}
    </div>
  );
}

export default Header;
